import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Box,
  AppBar,
  Toolbar,
  Grid,
  Card,
  CardContent,
  TablePagination,
  InputAdornment,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Divider,
  Checkbox,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ShoppingCart as OrdersIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  LocalShipping as ShippingIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Add as AddIcon,
  PictureAsPdf as PdfIcon,
  GetApp as ExportIcon,
  Phone as PhoneIcon,
  Receipt as ReceiptIcon,
  Image as ImageIcon,
  ListAlt as ListAltIcon,
  PersonAdd as PersonAddIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config/api';
import adminAuthService from '../services/adminAuthService';
import { useSnackbar } from 'notistack';
import * as XLSX from 'xlsx';

const OrderManagement = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // RTL theme configuration
  const rtlTheme = createTheme({
    direction: 'rtl',
    typography: {
      fontFamily: '"Cairo", "Arial", sans-serif'
    },
    components: {
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderRadius: '12px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            border: '1px solid #e0e7ff',
            overflow: 'hidden'
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: {
            minWidth: '100%'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: '#f8fafc',
            '& .MuiTableCell-head': {
              backgroundColor: '#f8fafc',
              fontWeight: 700,
              fontSize: '0.9rem',
              color: '#374151',
              borderBottom: '2px solid #e5e7eb',
              padding: '16px 12px'
            }
          }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            textAlign: 'right',
            padding: '12px',
            borderBottom: '1px solid #f1f5f9',
            fontSize: '0.875rem'
          },
          head: {
            backgroundColor: '#f8fafc',
            fontWeight: 700,
            fontSize: '0.9rem',
            color: '#374151',
            borderBottom: '2px solid #e5e7eb'
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: '#f8fafc',
              transition: 'background-color 0.2s ease'
            },
            '&:last-child .MuiTableCell-root': {
              borderBottom: 'none'
            }
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              textAlign: 'right'
            }
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            direction: 'rtl'
          }
        }
      }
    }
  });
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [wholesaleFilter, setWholesaleFilter] = useState('');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [deliveryDateFilter, setDeliveryDateFilter] = useState('');
  const [customDeliveryDate, setCustomDeliveryDate] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [expandedRow, setExpandedRow] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [editedOrder, setEditedOrder] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newProductQuantity, setNewProductQuantity] = useState(1);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]); // Array of {product, quantity}
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');

  // PDF Export state
  const [selectedOrdersForExport, setSelectedOrdersForExport] = useState([]);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [selectAllOrders, setSelectAllOrders] = useState(false);

  // Products List Export state
  const [productsListExportDialogOpen, setProductsListExportDialogOpen] = useState(false);
  const [exportingProductsList, setExportingProductsList] = useState(false);

  // Admin Notes state
  const [isEditingAdminNotes, setIsEditingAdminNotes] = useState(false);
  const [adminNotesValue, setAdminNotesValue] = useState('');
  const [savingAdminNotes, setSavingAdminNotes] = useState(false);

  // Wholesale Traders state
  const [savingToWholesale, setSavingToWholesale] = useState(null); // Track which order is being saved

  // Create Order state
  const [createOrderDialogOpen, setCreateOrderDialogOpen] = useState(false);
  const [newOrder, setNewOrder] = useState({
    customer_name: '',
    customer_phone: '',
    city: '',
    shipping_address: '',
    notes: '',
    status: 'pending'
  });
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCity, setCustomCity] = useState('');

  // Statistics state
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    confirmed: 0,
    shipped: 0,
    delivered: 0,
    cancelled: 0,
    totalRevenue: 0
  });

  // Notification state
  const [lastOrderCount, setLastOrderCount] = useState(0);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const autoRefreshIntervalRef = useRef(null);

  const cities = [
    'بغداد',
    'البصرة',
    'أربيل',
    'النجف',
    'كربلاء',
    'السليمانية',
    'الموصل',
    'دهوك',
    'بابل',
    'كركوك',
    'ذي قار',
    'المثنى',
    'القادسية',
    'ميسان',
    'صلاح الدين',
    'ديالى',
    'الأنبار',
    'واسط'
  ];

  const statusOptions = [
    { value: 'pending', label: 'في الانتظار', color: 'warning', icon: <PendingIcon /> },
    { value: 'confirmed', label: 'مؤكد', color: 'info', icon: <CheckIcon /> },
    { value: 'shipped', label: 'تم الشحن', color: 'primary', icon: <ShippingIcon /> },
    { value: 'delivered', label: 'تم التوصيل', color: 'success', icon: <CheckIcon /> },
    { value: 'cancelled', label: 'ملغي', color: 'error', icon: <CancelIcon /> }
  ];

  // Helper function for status colors
  const getStatusColor = (colorType) => {
    const colors = {
      warning: '#f59e0b',
      info: '#3b82f6', 
      primary: '#6366f1',
      success: '#10b981',
      error: '#ef4444'
    };
    return colors[colorType] || '#6b7280';
  };

  // WhatsApp function
  const handleWhatsAppClick = (phoneNumber) => {
    if (!phoneNumber) return;
    
    // Clean and format Iraqi phone number
    let cleanPhone = phoneNumber.replace(/[\s\-()]/g, '');
    
    // Convert Iraqi format to international format
    if (cleanPhone.startsWith('07')) {
      cleanPhone = '964' + cleanPhone.substring(1);
    } else if (cleanPhone.startsWith('+964')) {
      cleanPhone = cleanPhone.substring(1);
    } else if (!cleanPhone.startsWith('964')) {
      cleanPhone = '964' + cleanPhone;
    }
    
    const whatsappUrl = `https://wa.me/${cleanPhone}`;
    window.open(whatsappUrl, '_blank');
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php`;
      const token = adminAuthService.getToken();
      
      console.log('=== LoadOrders Debug ===');
      console.log('Making request to:', apiUrl);
      console.log('Token exists:', !!token);
      console.log('Full request headers:', {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      });
      
      console.log('Starting fetch request...');
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        console.error('HTTP Error:', response.status, response.statusText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const text = await response.text();
      console.log('Raw response text:', text.substring(0, 500) + (text.length > 500 ? '...' : ''));
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error('JSON Parse Error:', parseError);
        console.error('Failed to parse:', text);
        throw new Error('Invalid JSON response from server');
      }
      
      console.log('Parsed response:', data);
      
      if (data.success) {
        console.log('Orders loaded successfully:', data.orders?.length || 0);
        setOrders(data.orders || []);
        calculateStats(data.orders || []);
        setError(''); // Clear any existing errors
      } else {
        console.error('API returned error:', data.message);
        setError(data.message || 'حدث خطأ في تحميل الطلبات');
      }
    } catch (error) {
      console.error('=== LoadOrders Error ===');
      console.error('Error type:', error.constructor.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      setError('حدث خطأ في الاتصال بالخادم: ' + error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get snackbar from notistack
  const { enqueueSnackbar } = useSnackbar();

  // Function to show notification for new orders using notistack
  const showNewOrderNotification = useCallback((newOrdersCount) => {
    const message = newOrdersCount === 1 
      ? '🛒 لديك طلب جديد!' 
      : `🛒 لديك ${newOrdersCount} طلب جديد!`;
    
    enqueueSnackbar(message, {
      variant: 'success',
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'right',
      },
      autoHideDuration: 8000,
      style: {
        fontSize: '16px',
        fontFamily: '"Cairo", "Arial", sans-serif',
        direction: 'rtl'
      }
    });
    
    // Play notification sound
    const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZUQ0PVKvi8bllHAU2jdXzzn0pBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwY0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwY0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwKGGW55+mgUhELTKXh8LdnHwU0i9Pz0n8qBSh+zPHajzwK');
    audio.volume = 0.5;
    audio.play().catch(err => console.log('Audio play failed:', err));
  }, [enqueueSnackbar]);

  // Enhanced loadOrders with notification check
  const loadOrdersWithNotification = useCallback(async () => {
    try {
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php`;
      const token = adminAuthService.getToken();
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const text = await response.text();
      const data = JSON.parse(text);
      
      if (data.success) {
        const newOrders = data.orders || [];
        const newOrderCount = newOrders.length;
        
        // Check for new orders and show notification
        if (lastOrderCount > 0 && newOrderCount > lastOrderCount) {
          const newOrdersAdded = newOrderCount - lastOrderCount;
          showNewOrderNotification(newOrdersAdded);
        }
        
        setOrders(newOrders);
        setLastOrderCount(newOrderCount);
        calculateStats(newOrders);
        setError('');
      } else {
        setError(data.message || 'حدث خطأ في تحميل الطلبات');
      }
    } catch (error) {
      console.error('LoadOrders Error:', error);
      // Don't show error on background refresh to avoid annoying users
    }
  }, [lastOrderCount, showNewOrderNotification]);

  useEffect(() => {
    console.log('=== OrderManagement Debug ===');
    console.log('Component mounted');
    console.log('API_BASE_URL:', API_BASE_URL);
    console.log('Admin authenticated:', adminAuthService.isAuthenticated());
    console.log('Admin token exists:', !!adminAuthService.getToken());
    if (adminAuthService.getToken()) {
      console.log('Token preview:', adminAuthService.getToken().substring(0, 50) + '...');
    }
    loadOrders();
  }, [loadOrders]);

  const filterOrders = useCallback(() => {
    let filtered = orders;

    if (searchQuery) {
      filtered = filtered.filter(order =>
        order.id.toString().includes(searchQuery) ||
        (order.order_code && order.order_code.toString().includes(searchQuery)) ||
        (order.customer_name && order.customer_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (order.customer_email && order.customer_email.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (order.customer_phone && order.customer_phone.includes(searchQuery))
      );
    }

    if (statusFilter) {
      filtered = filtered.filter(order => order.status === statusFilter);
    }

    if (dateFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at).toISOString().split('T')[0];
        return orderDate === today;
      });
    } else if (dateFilter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at).toISOString().split('T')[0];
        return orderDate === yesterdayStr;
      });
    } else if (dateFilter === 'last7days') {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(order => new Date(order.created_at) >= sevenDaysAgo);
    } else if (dateFilter === 'last30days') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(order => new Date(order.created_at) >= thirtyDaysAgo);
    } else if (dateFilter === 'custom' && customStartDate && customEndDate) {
      const startDate = new Date(customStartDate);
      const endDate = new Date(customEndDate);
      endDate.setHours(23, 59, 59, 999); // Include the entire end date
      filtered = filtered.filter(order => {
        const orderDate = new Date(order.created_at);
        return orderDate >= startDate && orderDate <= endDate;
      });
    }

    if (wholesaleFilter === 'wholesale') {
      filtered = filtered.filter(order => order.is_wholesale === 1);
    } else if (wholesaleFilter === 'retail') {
      filtered = filtered.filter(order => order.is_wholesale === 0 || !order.is_wholesale);
    }

    // Filter by delivery date
    if (deliveryDateFilter === 'today') {
      const today = new Date().toISOString().split('T')[0];
      filtered = filtered.filter(order => {
        if (!order.shipped_at) return false;
        const deliveryDate = new Date(order.shipped_at).toISOString().split('T')[0];
        return deliveryDate === today;
      });
    } else if (deliveryDateFilter === 'yesterday') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      filtered = filtered.filter(order => {
        if (!order.shipped_at) return false;
        const deliveryDate = new Date(order.shipped_at).toISOString().split('T')[0];
        return deliveryDate === yesterdayStr;
      });
    } else if (deliveryDateFilter === 'tomorrow') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      filtered = filtered.filter(order => {
        if (!order.shipped_at) return false;
        const deliveryDate = new Date(order.shipped_at).toISOString().split('T')[0];
        return deliveryDate === tomorrowStr;
      });
    } else if (deliveryDateFilter === 'custom' && customDeliveryDate) {
      filtered = filtered.filter(order => {
        if (!order.shipped_at) return false;
        const deliveryDate = new Date(order.shipped_at).toISOString().split('T')[0];
        return deliveryDate === customDeliveryDate;
      });
    }

    // Sort by creation date (newest first)
    filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

    setFilteredOrders(filtered);
  }, [orders, searchQuery, statusFilter, dateFilter, wholesaleFilter, customStartDate, customEndDate]);

  useEffect(() => {
    filterOrders();
  }, [orders, searchQuery, statusFilter, dateFilter, wholesaleFilter, customStartDate, customEndDate, deliveryDateFilter, customDeliveryDate, filterOrders]);

  // Setup auto-refresh interval
  useEffect(() => {
    loadOrders(); // Initial load
    setLastOrderCount(orders.length);
    
    // Auto-refresh with notification check
    if (autoRefresh) {
      autoRefreshIntervalRef.current = setInterval(() => {
        console.log('Auto-refreshing orders with notification check...');
        loadOrdersWithNotification();
      }, 30000); // Check every 30 seconds
    }
    
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
  }, [autoRefresh, loadOrdersWithNotification]);

  // Toggle auto-refresh
  const toggleAutoRefresh = () => {
    setAutoRefresh(prev => !prev);
  };



  useEffect(() => {
    filterOrders();
  }, [orders, searchQuery, statusFilter, dateFilter, wholesaleFilter, customStartDate, customEndDate, filterOrders]);

  // Load products when user searches in Add Product dialog or changes filters
  useEffect(() => {
    if (!addProductDialogOpen) return;
    
    const hasSearchQuery = productSearchQuery.trim().length >= 2;
    const hasFilters = categoryFilter || brandFilter;
    
    // Only load if there's a search query (min 2 chars) OR filters are selected
    if (hasSearchQuery || hasFilters) {
      const delayDebounce = setTimeout(() => {
        loadAvailableProducts();
      }, 500); // Debounce for 500ms
      return () => clearTimeout(delayDebounce);
    } else {
      // Clear products when no search/filters to prevent showing all products
      setAvailableProducts([]);
    }
  }, [productSearchQuery, categoryFilter, brandFilter, addProductDialogOpen]);

  const calculateStats = (ordersList) => {
    const total = ordersList.length;
    const pending = ordersList.filter(o => o.status === 'pending').length;
    const confirmed = ordersList.filter(o => o.status === 'confirmed').length;
    const shipped = ordersList.filter(o => o.status === 'shipped').length;
    const delivered = ordersList.filter(o => o.status === 'delivered').length;
    const cancelled = ordersList.filter(o => o.status === 'cancelled').length;
    
    const totalRevenue = ordersList
      .filter(o => o.status === 'delivered')
      .reduce((sum, order) => sum + parseFloat(order.total_amount || 0), 0);

    setStats({ total, pending, confirmed, shipped, delivered, cancelled, totalRevenue });
  };

  const handleViewOrderDetails = async (order) => {
    try {
      console.log('=== ViewOrderDetails Debug ===');
      console.log('Requesting details for order:', order.id);
      
      // Always fetch fresh data from backend regardless of what's in the orders list
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php?order_id=${order.id}&_t=${Date.now()}`;
      console.log('Request URL:', apiUrl);
      
      // Load full order details including items
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('Order details response:', {
        status: response.status,
        ok: response.ok
      });

      const data = await response.json();
      console.log('Order details data:', data);
      console.log('Order object keys:', Object.keys(data.order || {}));
      console.log('Admin notes field exists?', 'admin_notes' in (data.order || {}));
      console.log('Admin notes value:', data.order?.admin_notes);
      console.log('Admin notes type:', typeof data.order?.admin_notes);
      
      if (response.ok && data.success) {
        const orderData = data.order;
        
        // Normalize items to have consistent structure
        if (orderData.items) {
          orderData.items = orderData.items.map(item => ({
            ...item,
            id: item.order_item_id, // Use order_item_id as the primary id for frontend
            order_item_id: item.order_item_id, // Keep this for backend communication
            is_new: false // All items from backend are existing items
          }));
        }
        
        console.log('Order details loaded (fresh from backend):', orderData);
        console.log('Admin notes value from backend:', orderData.admin_notes);
        console.log('Items with prices:', orderData.items?.map(item => ({
          name: item.product_name,
          price: item.price,
          priceType: typeof item.price,
          quantity: item.quantity,
          weight: item.weight,
          orderItemId: item.order_item_id
        })));
        setSelectedOrder(orderData);
        
        // Create edited order - use delivery price from database, don't recalculate
        const editedOrderCopy = JSON.parse(JSON.stringify(orderData));
        
        setEditedOrder(editedOrderCopy);
        setHasChanges(false);
        
        // Initialize admin notes
        console.log('Initializing admin notes with value:', orderData.admin_notes);
        setAdminNotesValue(orderData.admin_notes || '');
        setIsEditingAdminNotes(false);
        
        // Clear any existing messages when opening the dialog
        setError('');
        setSuccess('');
        setDetailsDialogOpen(true);
      } else {
        setError(data.message || 'فشل في تحميل تفاصيل الطلب');
      }
    } catch (error) {
      console.error('ViewOrderDetails error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  const handleUpdateOrderStatus = async () => {
    try {
      console.log('=== UpdateOrderStatus Debug ===');
      console.log('Updating order:', selectedOrder.id, 'to status:', newStatus);
      
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php`;
      const requestBody = {
        action: 'update_status',
        order_id: selectedOrder.id,
        status: newStatus
      };
      
      console.log('Request URL:', apiUrl);
      console.log('Request body:', requestBody);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Update status response:', {
        status: response.status,
        ok: response.ok
      });

      const data = await response.json();
      console.log('Update status data:', data);

      if (response.ok && data.success) {
        setSuccess('تم تحديث حالة الطلب بنجاح');
        setStatusDialogOpen(false);
        setSelectedOrder(null);
        setNewStatus('');
        loadOrders();
      } else {
        setError(data.message || 'فشل في تحديث حالة الطلب');
      }
    } catch (error) {
      console.error('UpdateOrderStatus error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
    }
  };

  // Handle inline status change
  const handleStatusChange = async (orderId, newStatusValue) => {
    try {
      console.log('=== Inline Status Update Debug ===');
      console.log('Updating order:', orderId, 'to status:', newStatusValue);
      
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php`;
      const requestBody = {
        action: 'update_status',
        order_id: orderId,
        status: newStatusValue
      };
      
      console.log('Request URL:', apiUrl);
      console.log('Request body:', requestBody);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('Inline status update response:', {
        status: response.status,
        ok: response.ok
      });

      const data = await response.json();
      console.log('Inline status update data:', data);

      if (response.ok && data.success) {
        setSuccess('تم تحديث حالة الطلب بنجاح');
        
        // Update the order status in the local state immediately
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId 
              ? { ...order, status: newStatusValue }
              : order
          )
        );
        
        // Recalculate stats with updated orders
        const updatedOrders = orders.map(order => 
          order.id === orderId 
            ? { ...order, status: newStatusValue }
            : order
        );
        calculateStats(updatedOrders);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في تحديث حالة الطلب');
        // Clear error message after 5 seconds
        setTimeout(() => setError(''), 5000);
      }
    } catch (error) {
      console.error('Inline status update error:', error);
      setError('حدث خطأ في الاتصال بالخادم');
      setTimeout(() => setError(''), 5000);
    }
  };

  // Handle status filter from stats cards
  const handleStatusFilter = (status) => {
    console.log('Filtering by status:', status);
    if (statusFilter === status) {
      // If clicking the same status, clear the filter
      setStatusFilter('');
    } else {
      // Apply the new status filter
      setStatusFilter(status);
    }
    // Reset to first page when filtering
    setPage(0);
  };

  // Function to recalculate order totals based on items
  // Function to round delivery price DOWN to nearest 250 increment
  // Examples: 5001-5249 → 5000, 5250-5499 → 5250, 5500-5749 → 5500, 5750-5999 → 5750
  const roundDeliveryPrice = (price) => {
    return Math.floor(price / 250) * 250;
  };

  const recalculateOrderTotals = (orderItems, originalOrder) => {
    console.log('=== Recalculating Order Totals ===');
    console.log('Items:', orderItems);
    console.log('Original order delivery:', originalOrder.delivery);
    console.log('Original order discount:', originalOrder.discount_amount);
    
    if (!orderItems || orderItems.length === 0) {
      // Minimum delivery cost is 5000 IQD even with no items
      const minDelivery = 5000;
      const result = {
        subtotal: 0,
        delivery: minDelivery,
        total_amount: minDelivery,
        after_discount_amount: minDelivery
      };
      console.log('No items - returning with minimum delivery:', result);
      return result;
    }

    // Calculate subtotal and total weight from items
    let subtotal = 0;
    let totalWeight = 0;
    
    orderItems.forEach(item => {
      const quantity = parseFloat(item.quantity || 0);
      const price = parseFloat(item.price || 0);
      const weight = parseFloat(item.weight || 0);
      const itemTotal = quantity * price;
      const itemWeight = weight * quantity;
      
      console.log(`Item: ${item.product_name}, Qty: ${quantity}, Price: ${price}, Weight: ${weight}kg, Item Total: ${itemTotal}, Item Weight: ${itemWeight}kg`);
      
      subtotal += itemTotal;
      totalWeight += itemWeight;
    });
    
    console.log(`Subtotal: ${subtotal}, Total Weight: ${totalWeight}kg`);

    // Calculate delivery cost based on weight
    // Minimum delivery cost is 5000 IQD
    let deliveryCost = 5000;
    
    // If weight exceeds 5kg, add 1000 IQD per additional kg
    if (totalWeight > 5) {
      const additionalWeight = totalWeight - 5;
      deliveryCost += additionalWeight * 1000;
    }

    // Round delivery price to nearest 250 increment
    deliveryCost = roundDeliveryPrice(deliveryCost);
    
    console.log(`Delivery cost: ${deliveryCost} IQD (based on ${totalWeight}kg)`);

    // Calculate total with delivery
    const totalAmount = subtotal + deliveryCost;

    // Apply discount if exists
    const discountAmount = parseFloat(originalOrder.discount_amount || 0);
    const afterDiscountAmount = Math.max(0, totalAmount - discountAmount);

    const result = {
      subtotal,
      delivery: deliveryCost,
      total_amount: totalAmount,
      after_discount_amount: afterDiscountAmount
    };
    
    console.log('Calculation result:', result);
    return result;
  };

  // Item editing functions
  const handleUpdateItemQuantity = (itemIndex, newQuantity) => {
    if (!editedOrder || !editedOrder.items) return;
    
    const updatedItems = [...editedOrder.items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      quantity: newQuantity
    };
    
    // Recalculate totals including delivery
    const newTotals = recalculateOrderTotals(updatedItems, editedOrder);
    
    setEditedOrder({
      ...editedOrder,
      items: updatedItems,
      delivery: newTotals.delivery,
      total_amount: newTotals.total_amount,
      after_discount_amount: newTotals.after_discount_amount
    });
    setHasChanges(true);
  };

  const handleUpdateItemPrice = (itemIndex, newPrice) => {
    if (!editedOrder || !editedOrder.items) return;
    
    const updatedItems = [...editedOrder.items];
    updatedItems[itemIndex] = {
      ...updatedItems[itemIndex],
      price: newPrice
    };
    
    // Recalculate totals including delivery
    const newTotals = recalculateOrderTotals(updatedItems, editedOrder);
    
    setEditedOrder({
      ...editedOrder,
      items: updatedItems,
      delivery: newTotals.delivery,
      total_amount: newTotals.total_amount,
      after_discount_amount: newTotals.after_discount_amount
    });
    setHasChanges(true);
  };

  const handleRemoveItem = (itemIndex) => {
    if (!editedOrder || !editedOrder.items) return;
    
    const updatedItems = editedOrder.items.filter((_, index) => index !== itemIndex);
    
    // Recalculate totals including delivery
    const newTotals = recalculateOrderTotals(updatedItems, editedOrder);
    
    setEditedOrder({
      ...editedOrder,
      items: updatedItems,
      delivery: newTotals.delivery,
      total_amount: newTotals.total_amount,
      after_discount_amount: newTotals.after_discount_amount
    });
    setHasChanges(true);
  };

  const handleUpdateDiscount = (newDiscount) => {
    if (!editedOrder) return;
    
    const discountAmount = parseFloat(newDiscount) || 0;
    
    // Calculate after discount amount
    const totalAmount = parseFloat(editedOrder.total_amount || 0);
    const afterDiscountAmount = Math.max(0, totalAmount - discountAmount);
    
    setEditedOrder({
      ...editedOrder,
      discount_amount: discountAmount,
      after_discount_amount: afterDiscountAmount
    });
    setHasChanges(true);
  };

  const hasOrderChanges = () => {
    return hasChanges;
  };

  const handleSaveOrderChanges = async () => {
    console.log('Save button clicked');
    console.log('editedOrder:', editedOrder);
    console.log('hasChanges:', hasChanges);
    
    if (!editedOrder || !hasChanges) {
      console.log('Exiting early - no edited order or no changes');
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      console.log('Starting order save process...');
      
      // Prepare items data with proper identification
      const itemsData = editedOrder.items ? editedOrder.items.map(item => {
        // For existing items, use order_item_id from backend (should be item.id if from backend)
        // For new items, order_item_id should be null
        const isNewItem = item.is_new || String(item.id || '').startsWith('new_');
        const orderItemId = isNewItem ? null : (item.order_item_id || item.id);
        
        return {
          order_item_id: orderItemId, // Send order_item_id for existing items, null for new
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.product_sku || '',
          brand_name: item.brand_name || '',
          quantity: parseInt(item.quantity) || 0,
          price: parseFloat(item.price) || 0,
          is_new: isNewItem,
          is_existing: !isNewItem
        };
      }) : [];

      // Prepare the order data for backend
      const orderData = {
        action: 'update_order',
        order_id: editedOrder.id,
        customer_name: editedOrder.customer_name,
        customer_phone: editedOrder.customer_phone,
        customer_email: editedOrder.customer_email,
        shipping_address: editedOrder.shipping_address,
        city: editedOrder.city,
        status: editedOrder.status,
        notes: editedOrder.notes,
        total_amount: editedOrder.total_amount,
        delivery: editedOrder.delivery,
        after_discount_amount: editedOrder.after_discount_amount,
        discount_amount: editedOrder.discount_amount,
        coupon_code: editedOrder.coupon_code,
        payment_method: editedOrder.payment_method,
        items: itemsData,
        // Add flags to help backend understand what we're trying to do
        update_items: true,
        recalculate_totals: true,
        items_changed: true
      };

      console.log('=== DETAILED SAVE DEBUG ===');
      console.log('Saving order changes:', orderData);
      console.log('Items being saved (count):', orderData.items?.length || 0);
      console.log('Items identification:', orderData.items?.map(item => ({
        id: item.order_item_id || 'NEW',
        product_name: item.product_name,
        quantity: item.quantity,
        price: item.price,
        is_new: item.is_new,
        is_existing: item.is_existing
      })));
      console.log('Items being saved (detailed):', JSON.stringify(orderData.items, null, 2));
      console.log('Expected totals:', {
        itemsSubtotal: orderData.items?.reduce((sum, item) => sum + (item.quantity * parseFloat(item.price)), 0) || 0,
        delivery: editedOrder.delivery,
        calculatedTotal: editedOrder.total_amount,
        afterDiscount: editedOrder.after_discount_amount
      });
      console.log('Full order data being sent:', JSON.stringify(orderData, null, 2));

      const token = await adminAuthService.getToken();
      console.log('Got token:', token ? 'Yes' : 'No');
      
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(orderData)
      });

      console.log('Response status:', response.status);
      const result = await response.json();
      console.log('=== BACKEND RESPONSE DEBUG ===');
      console.log('Save order response:', result);
      console.log('Backend response (detailed):', JSON.stringify(result, null, 2));

      if (result.success) {
        console.log('Save successful, updating local state...');
        
        // If the backend returned updated order data, use it
        if (result.data && result.data.order) {
          console.log('Backend returned updated order:', result.data.order);
          const backendOrder = result.data.order;
          
          // Normalize items to have consistent structure
          if (backendOrder.items) {
            backendOrder.items = backendOrder.items.map(item => ({
              ...item,
              id: item.order_item_id, // Use order_item_id as the primary id for frontend
              order_item_id: item.order_item_id, // Keep this for backend communication
              is_new: false // All items from backend are existing items
            }));
          }
          
          setSelectedOrder(backendOrder);
          
          // Create fresh edited copy with recalculated totals
          const editedOrderCopy = JSON.parse(JSON.stringify(backendOrder));
          const recalculatedTotals = recalculateOrderTotals(editedOrderCopy.items, editedOrderCopy);
          editedOrderCopy.total_amount = recalculatedTotals.total_amount;
          editedOrderCopy.after_discount_amount = recalculatedTotals.after_discount_amount;
          setEditedOrder(editedOrderCopy);
          
          // Update the local orders state with backend data
          const updatedOrders = orders.map(order => {
            if (order.id === editedOrder.id) {
              return backendOrder;
            }
            return order;
          });
          setOrders(updatedOrders);
          calculateStats(updatedOrders);
          setHasChanges(false); // Clear changes since we have fresh backend data
        } else {
          console.log('Backend did not return updated order data, using local state');
          // Update the selectedOrder with new values for display INCLUDING the updated items
          const updatedSelectedOrder = {
            ...selectedOrder,
            customer_name: editedOrder.customer_name,
            customer_phone: editedOrder.customer_phone,
            shipping_address: editedOrder.shipping_address,
            city: editedOrder.city,
            status: editedOrder.status,
            notes: editedOrder.notes,
            total_amount: editedOrder.total_amount,
            delivery: editedOrder.delivery,
            after_discount_amount: editedOrder.after_discount_amount,
            items: editedOrder.items // IMPORTANT: Include the updated items!
          };
          setSelectedOrder(updatedSelectedOrder);
          
          // Update the local orders state immediately to reflect changes
          const updatedOrders = orders.map(order => {
            if (order.id === editedOrder.id) {
              return {
                ...order,
                customer_name: editedOrder.customer_name,
                customer_phone: editedOrder.customer_phone,
                shipping_address: editedOrder.shipping_address,
                city: editedOrder.city,
                status: editedOrder.status,
                notes: editedOrder.notes,
                total_amount: editedOrder.total_amount,
                delivery: editedOrder.delivery,
                after_discount_amount: editedOrder.after_discount_amount
              };
            }
            return order;
          });
          setOrders(updatedOrders);
          calculateStats(updatedOrders);
          console.log('Local state updated with new totals:', {
            orderId: editedOrder.id,
            oldTotal: orders.find(o => o.id === editedOrder.id)?.total_amount,
            newTotal: editedOrder.total_amount,
            oldAfterDiscount: orders.find(o => o.id === editedOrder.id)?.after_discount_amount,
            newAfterDiscount: editedOrder.after_discount_amount
          });
          
          // Try to refresh from backend to get updated order_item_ids
          refreshOrderDetails(editedOrder.id);
        }
        
        setSuccess(`تم حفظ التغييرات بنجاح - المبلغ النهائي: ${parseFloat(editedOrder.after_discount_amount || editedOrder.total_amount).toLocaleString()} د.ع`);
        
        // Refresh the main orders list to ensure fresh data for future View operations
        loadOrders();
        
      } else {
        throw new Error(result.message || 'فشل في حفظ التغييرات');
      }
      
    } catch (error) {
      console.error('Error saving order changes:', error);
      setError('فشل في حفظ التغييرات: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Function to validate phone number
  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^07\d{9}$/;
    return phoneRegex.test(phone);
  };

  // Function to create new order
  const handleCreateOrder = async () => {
    setCreatingOrder(true);
    try {
      // Validate required fields
      if (!newOrder.customer_name || !newOrder.customer_phone || !newOrder.city || !newOrder.shipping_address) {
        setError('الرجاء ملء جميع الحقول المطلوبة');
        setCreatingOrder(false);
        return;
      }

      // Validate phone format
      if (!validatePhoneNumber(newOrder.customer_phone)) {
        setError('رقم الهاتف يجب أن يكون بصيغة 07XXXXXXXXX (11 رقم)');
        setPhoneError('رقم الهاتف يجب أن يبدأ بـ 07 ويتكون من 11 رقم');
        setCreatingOrder(false);
        return;
      }

      // Use custom city if "أخرى" is selected
      const finalCity = newOrder.city === 'أخرى' ? customCity : newOrder.city;
      
      if (newOrder.city === 'أخرى' && !customCity.trim()) {
        setError('الرجاء إدخال اسم المدينة');
        setCreatingOrder(false);
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'create_order',
          ...newOrder,
          city: finalCity
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم إنشاء الطلب بنجاح - يمكنك الآن إضافة المنتجات');
        setCreateOrderDialogOpen(false);
        
        // Reset form
        setNewOrder({
          customer_name: '',
          customer_phone: '',
          city: '',
          shipping_address: '',
          notes: '',
          status: 'pending'
        });
        setShowCustomCity(false);
        setCustomCity('');
        setPhoneError('');
        
        // Reload orders to get the new order in the list
        await loadOrders();
        
        // Automatically open the order details to add products
        if (data.data && data.data.order_id) {
          // Small delay to ensure orders list is updated
          setTimeout(async () => {
            try {
              // Fetch the newly created order details
              const orderResponse = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${data.data.order_id}`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${adminAuthService.getToken()}`,
                  'Content-Type': 'application/json'
                }
              });
              
              const orderData = await orderResponse.json();
              
              if (orderData.success && orderData.order) {
                // Open order details dialog with the newly created order
                handleViewOrderDetails(orderData.order);
              }
            } catch (error) {
              console.error('Error loading new order details:', error);
            }
          }, 300);
        }
      } else {
        setError(data.message || 'فشل في إنشاء الطلب');
      }
    } catch (error) {
      console.error('Error creating order:', error);
      setError('فشل في إنشاء الطلب: ' + error.message);
    } finally {
      setCreatingOrder(false);
    }
  };

  // Function to save admin notes
  const handleSaveAdminNotes = async () => {
    if (!selectedOrder) return;
    
    setSavingAdminNotes(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_orders.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'update_order',
          order_id: selectedOrder.id,
          admin_notes: adminNotesValue
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم حفظ الملاحظات بنجاح');
        
        // Update selected order
        setSelectedOrder(prev => ({
          ...prev,
          admin_notes: adminNotesValue
        }));
        
        // Update orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === selectedOrder.id 
              ? { ...order, admin_notes: adminNotesValue }
              : order
          )
        );
        
        setIsEditingAdminNotes(false);
        
        // Clear success message after 3 seconds
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(data.message || 'فشل في حفظ الملاحظات');
      }
    } catch (error) {
      console.error('Error saving admin notes:', error);
      setError('فشل في حفظ الملاحظات: ' + error.message);
    } finally {
      setSavingAdminNotes(false);
    }
  };

  // Function to refresh order details and get updated totals
  const refreshOrderDetails = async (orderId) => {
    try {
      console.log('Refreshing order details for order:', orderId);
      const apiUrl = `${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}&_t=${Date.now()}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();
      console.log('Refresh order details response:', data);
      
      if (response.ok && data.success && data.order) {
        const updatedOrder = data.order;
        
        // Normalize items to have consistent structure
        if (updatedOrder.items) {
          updatedOrder.items = updatedOrder.items.map(item => ({
            ...item,
            id: item.order_item_id, // Use order_item_id as the primary id for frontend
            order_item_id: item.order_item_id, // Keep this for backend communication
            is_new: false // All items from backend are existing items
          }));
        }
        
        console.log('Order details refreshed successfully:', updatedOrder);
        
        // Only update if we're still viewing the same order
        if (selectedOrder && selectedOrder.id === orderId) {
          console.log('Updating selectedOrder and editedOrder with fresh backend data');
          console.log('Old editedOrder items count:', editedOrder?.items?.length || 0);
          console.log('New backend items count:', updatedOrder.items?.length || 0);
          console.log('New backend items IDs:', updatedOrder.items?.map(item => ({ 
            id: item.id, 
            order_item_id: item.order_item_id,
            product_name: item.product_name,
            quantity: item.quantity,
            price: item.price 
          })));
          
          setSelectedOrder(updatedOrder);
          
          // Create fresh edited copy with recalculated totals
          const editedOrderCopy = JSON.parse(JSON.stringify(updatedOrder));
          const recalculatedTotals = recalculateOrderTotals(editedOrderCopy.items, editedOrderCopy);
          editedOrderCopy.total_amount = recalculatedTotals.total_amount;
          editedOrderCopy.after_discount_amount = recalculatedTotals.after_discount_amount;
          
          console.log('Setting new editedOrder with fresh item IDs');
          setEditedOrder(editedOrderCopy);
          
          // Clear the hasChanges flag since we now have fresh backend data
          setHasChanges(false);
        }
        
        // Also update the order in the main orders list
        setOrders(prevOrders => 
          prevOrders.map(order => 
            order.id === orderId ? updatedOrder : order
          )
        );
      } else {
        console.warn('Failed to refresh order details:', data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error refreshing order details:', error);
    }
  };

  // Load categories
  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_categories.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setCategories(data.categories || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Load brands
  const loadBrands = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_brands.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        }
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setBrands(data.brands || []);
      }
    } catch (error) {
      console.error('Error loading brands:', error);
      setBrands([]);
    }
  };

  // Load available products for adding to order
  const loadAvailableProducts = async () => {
    try {
      setLoadingProducts(true);
      console.log('Loading products from admin API...');
      
      const response = await fetch(`${API_BASE_URL}/api/admin_products.php`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        }
      });

      console.log('Products API response:', response.status);
      const data = await response.json();
      console.log('Products data:', data);
      
      if (response.ok && data.success) {
        setAvailableProducts(data.products || []);
        console.log('Loaded products:', data.products?.length || 0);
      } else {
        console.error('Failed to load products:', data.message);
        setError(data.message || 'فشل في تحميل المنتجات');
      }
    } catch (error) {
      console.error('Error loading products:', error);
      setError('حدث خطأ في تحميل المنتجات');
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleOpenAddProductDialog = () => {
    setAddProductDialogOpen(true);
    setSelectedProduct(null);
    setNewProductQuantity(1);
    setProductSearchQuery('');
    setAvailableProducts([]); // Clear products list
    setSelectedProducts([]); // Clear selected products
    setCategoryFilter('');
    setBrandFilter('');
    // Load categories and brands if not already loaded
    if (categories.length === 0) loadCategories();
    if (brands.length === 0) loadBrands();
  };

  const handleAddProductToOrder = () => {
    if (selectedProducts.length === 0 || !editedOrder) return;

    // Create new items from all selected products
    const newItems = selectedProducts.map((selectedItem, index) => {
      const product = selectedItem.product;
      const finalPrice = product.after_discount || product.sell_price || product.price || 0;
      
      return {
        id: `new_${Date.now()}_${index}`, // Temporary ID for new items with "new_" prefix
        order_item_id: null, // Null for new items
        is_new: true, // Flag to identify new items
        product_id: product.id,
        product_name: product.name,
        product_sku: product.sku || '',
        brand_name: product.brand_name || '',
        quantity: selectedItem.quantity,
        price: finalPrice,
        weight: product.weight || 0, // Include weight from product
        display_price: finalPrice,
        available: product.active === 1 || product.active === '1' ? 1 : 0,
        product_image: product.image_url || product.image || ''
      };
    });

    const updatedItems = [...(editedOrder.items || []), ...newItems];
    
    // Recalculate totals including delivery
    const newTotals = recalculateOrderTotals(updatedItems, editedOrder);
    
    setEditedOrder({
      ...editedOrder,
      items: updatedItems,
      delivery: newTotals.delivery,
      total_amount: newTotals.total_amount,
      after_discount_amount: newTotals.after_discount_amount
    });
    
    setHasChanges(true);
    setAddProductDialogOpen(false);
    
    const productCount = selectedProducts.length;
    setSuccess(`تم إضافة ${productCount} ${productCount === 1 ? 'منتج' : 'منتجات'} إلى الطلب`);
  };

  const getFilteredProducts = () => {
    let filtered = availableProducts;
    
    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(product => 
        product.category_id === parseInt(categoryFilter)
      );
    }
    
    // Apply brand filter
    if (brandFilter) {
      filtered = filtered.filter(product => 
        product.brand_id === parseInt(brandFilter)
      );
    }
    
    // Apply search query filter
    if (productSearchQuery) {
      const trimmedQuery = productSearchQuery.trim();
      
      // Check if search contains comma-separated SKUs
      if (trimmedQuery.includes(',')) {
        const skuTerms = trimmedQuery.split(',').map(term => term.trim().toLowerCase()).filter(term => term.length > 0);
        filtered = filtered.filter(product => 
          product.sku && skuTerms.includes(product.sku.toLowerCase())
        );
      } else {
        // Multi-word search - all words must appear in combined text
        const searchWords = trimmedQuery.toLowerCase().split(/\s+/).filter(word => word.length > 0);
        
        filtered = filtered.filter(product => {
          const productName = product.name?.toLowerCase() || '';
          const productSku = product.sku?.toLowerCase() || '';
          const productBarcode = product.barcode?.toLowerCase() || '';
          const productDesc = product.description?.toLowerCase() || '';
          const productCategory = product.category_name?.toLowerCase() || '';
          const productBrand = product.brand_name?.toLowerCase() || '';
          const combinedText = `${productName} ${productSku} ${productBarcode} ${productDesc} ${productCategory} ${productBrand}`;
          
          // Check if ALL search words appear in the combined text
          return searchWords.every(word => combinedText.includes(word));
        });
      }
    }
    
    return filtered;
  };

  // PDF Export functions
  const handleSelectOrderForExport = (orderId, isSelected) => {
    if (isSelected) {
      setSelectedOrdersForExport([...selectedOrdersForExport, orderId]);
    } else {
      setSelectedOrdersForExport(selectedOrdersForExport.filter(id => id !== orderId));
    }
  };

  const handleSelectAllOrdersForExport = () => {
    if (selectAllOrders) {
      // Deselect all orders on current page
      setSelectedOrdersForExport([]);
    } else {
      // Select all orders on current page
      const currentPageOrderIds = filteredOrders
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map(order => order.id);
      setSelectedOrdersForExport(currentPageOrderIds);
    }
    setSelectAllOrders(!selectAllOrders);
  };

  const generateOrdersPDF = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('Please select orders to export');
      return;
    }

    try {
      setExportingPdf(true);
      
      // Create the request to backend PDF generation using separate endpoint
      const requestBody = {
        order_ids: selectedOrdersForExport
      };
      
      console.log('Sending PDF export request:', requestBody);
      
      const response = await fetch(`${API_BASE_URL}/api/orders_pdf.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminAuthService.getToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      console.log('PDF export response status:', response.status);
      
      if (response.ok) {
        // Check if response is PDF (binary) or JSON (error)
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/pdf')) {
          // Get the PDF as a blob
          const blob = await response.blob();
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `orders_export_${new Date().toISOString().slice(0, 10)}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          setSuccess(`Successfully exported ${selectedOrdersForExport.length} orders`);
          setExportDialogOpen(false);
          setSelectedOrdersForExport([]);
          setSelectAllOrders(false);
        } else {
          // Response is JSON (error)
          const errorData = await response.json();
          setError('Error occurred while exporting orders: ' + (errorData.message || 'Unknown error'));
        }
      } else {
        // If response is not ok, try to parse error message
        try {
          const errorData = await response.json();
          setError('Error occurred while exporting orders: ' + (errorData.message || 'Server error'));
        } catch (parseError) {
          const errorText = await response.text();
          setError('Error occurred while exporting orders: ' + errorText);
        }
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      setError('Error occurred while exporting orders: ' + error.message);
    } finally {
      setExportingPdf(false);
    }
  };

  // Function to aggregate products from selected orders
  const aggregateProductsFromOrders = async () => {
    try {
      setExportingProductsList(true);
      
      if (selectedOrdersForExport.length === 0) {
        setError('الرجاء تحديد طلبات لتصدير قائمة المنتجات');
        return null;
      }

      // Fetch detailed order data for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(orderId =>
        fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        }).then(res => res.json())
      );

      const orderResults = await Promise.all(orderDetailsPromises);

      // Group items safely by SKU, with fallbacks to prevent accidental merging.
      const productsMap = new Map();

      orderResults.forEach(result => {
        if (result.success && result.order && Array.isArray(result.order.items)) {
          result.order.items.forEach(item => {
            let imageUrl = item.product_image || item.image_url || item.image || '';
            if (imageUrl && !imageUrl.startsWith('http')) {
              imageUrl = `${API_BASE_URL}/api/uploads/products/${imageUrl}`;
            }

            const productName = item.product_name || 'غير محدد';
            const productBrand = item.brand_name || '';
            const productSku = (item.sku || item.product_sku || '').toString().trim();
            const normalizedSku = productSku.toLowerCase();
            const productId = item.product_id || null;
            const salePrice = parseFloat(item.price || 0) || 0;
            const quantity = parseInt(item.quantity) || 0;

            let groupingKey = '';
            if (normalizedSku) {
              groupingKey = `SKU:${normalizedSku}`;
            } else if (productId) {
              groupingKey = `PID:${productId}`;
            } else {
              groupingKey = `NAME:${productName.toLowerCase()}|BRAND:${productBrand.toLowerCase()}|PRICE:${salePrice}`;
            }

            if (productsMap.has(groupingKey)) {
              const existing = productsMap.get(groupingKey);
              existing.quantity += quantity;
              if (!existing.image && imageUrl) {
                existing.image = imageUrl;
              }
            } else {
              productsMap.set(groupingKey, {
                key: groupingKey,
                name: productName,
                sku: productSku,
                brand: productBrand,
                quantity,
                image: imageUrl,
                product_id: productId,
                sale_price: salePrice
              });
            }
          });
        }
      });

      return Array.from(productsMap.values());
    } catch (error) {
      console.error('Error aggregating products:', error);
      setError('حدث خطأ في تجميع قائمة المنتجات: ' + error.message);
      return null;
    } finally {
      setExportingProductsList(false);
    }
  };

  // Generate Stock Update Excel for delivery company
  const generateStockExport = async () => {
    if (selectedOrdersForExport.length === 0) {
      setError('الرجاء تحديد طلبات للتصدير');
      return;
    }

    try {
      setExportingPdf(true);
      
      // Fetch full details for all selected orders
      const orderDetailsPromises = selectedOrdersForExport.map(async (orderId) => {
        const response = await fetch(`${API_BASE_URL}/api/admin_orders.php?order_id=${orderId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${adminAuthService.getToken()}`,
            'Content-Type': 'application/json'
          }
        });
        const data = await response.json();
        return data.success ? data.order : null;
      });

      const ordersWithDetails = await Promise.all(orderDetailsPromises);
      const validOrders = ordersWithDetails.filter(order => order !== null);

      // Aggregate all items from selected orders
      const allItems = [];
      const exportDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format

      validOrders.forEach(order => {
        if (order.items && Array.isArray(order.items)) {
          order.items.forEach(item => {
            allItems.push({
              'Invoice No.': '',
              'Customer name': 'online',
              'Customer Phone number': '7813438118',
              'Customer Email': 'alatharibaq@gmail.com',
              'Sale Date': exportDate,
              'Product name': item.product_name || '',
              'Product SKU': item.product_sku || '',
              'Quantity': parseInt(item.quantity) || 0,
              'Product Unit': 'قطعة',
              'Unit Price': parseFloat(item.purchase_price || 0),
              'Item Tax': '',
              'Item Discount': '',
              'Item Description': '',
              'Types of service': '',
              'Custom Field 1': '',
              'Custom Field 2': '',
              'Custom Field 3': '',
              'Custom Field 4': ''
            });
          });
        }
      });

      if (allItems.length === 0) {
        setError('لا توجد منتجات في الطلبات المحددة');
        setExportingPdf(false);
        return;
      }

      // Create workbook and worksheet
      const worksheet = XLSX.utils.json_to_sheet(allItems);
      
      // Set column widths
      const columnWidths = [
        { wch: 12 }, // Invoice No.
        { wch: 15 }, // Customer name
        { wch: 18 }, // Customer Phone number
        { wch: 25 }, // Customer Email
        { wch: 12 }, // Sale Date
        { wch: 30 }, // Product name
        { wch: 15 }, // Product SKU
        { wch: 10 }, // Quantity
        { wch: 12 }, // Product Unit
        { wch: 12 }, // Unit Price
        { wch: 12 }, // Item Tax
        { wch: 12 }, // Item Discount
        { wch: 20 }, // Item Description
        { wch: 15 }, // Types of service
        { wch: 15 }, // Custom Field 1
        { wch: 15 }, // Custom Field 2
        { wch: 15 }, // Custom Field 3
        { wch: 15 }  // Custom Field 4
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Stock Update');

      // Generate Excel file and download
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `stock_update_${new Date().toISOString().slice(0, 10)}.xlsx`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setSuccess(`تم تصدير ${allItems.length} منتج من ${validOrders.length} طلب`);
      setSelectedOrdersForExport([]);
      setSelectAllOrders(false);
      setExportingPdf(false);
    } catch (error) {
      console.error('Error generating stock export:', error);
      setError('حدث خطأ أثناء تصدير المخزون');
      setExportingPdf(false);
    }
  };

  // Function to save customer to wholesale traders list
  const handleSaveToWholesaleTraders = async (order) => {
    if (!order.customer_name || !order.customer_phone) {
      setError('اسم العميل ورقم الهاتف مطلوبان لإضافة العميل إلى قائمة تجار الجملة');
      return;
    }

    setSavingToWholesale(order.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin_wholesale_traders.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminAuthService.getToken()}`
        },
        body: JSON.stringify({
          action: 'add',
          customer_name: order.customer_name,
          customer_phone: order.customer_phone,
          customer_email: order.customer_email || '',
          city: order.city || '',
          shipping_address: order.shipping_address || '',
          notes: `تم الإضافة من الطلب #${order.id}`
        })
      });

      const data = await response.json();
      
      if (data.success) {
        setSuccess('تم إضافة العميل إلى قائمة تجار الجملة بنجاح');
      } else {
        if (data.message.includes('موجود')) {
          setError('هذا العميل موجود بالفعل في قائمة تجار الجملة');
        } else {
          setError(data.message || 'فشل في إضافة العميل إلى قائمة تجار الجملة');
        }
      }
    } catch (error) {
      console.error('Error saving to wholesale traders:', error);
      setError('حدث خطأ أثناء حفظ بيانات العميل');
    } finally {
      setSavingToWholesale(null);
    }
  };

  // Function to generate products list export
  const generateProductsListExport = async () => {
    const products = await aggregateProductsFromOrders();
    
    if (!products || products.length === 0) {
      setError('لا توجد منتجات للتصدير');
      return;
    }

    try {
      setExportingProductsList(true);

      const totalQuantity = products.reduce((sum, product) => sum + (parseInt(product.quantity) || 0), 0);
      
      // Create HTML content for export
      const htmlContent = `
        <!DOCTYPE html>
        <html dir="rtl" lang="ar">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>قائمة المنتجات</title>
          <style>
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
            }
            body {
              font-family: 'Cairo', Arial, sans-serif;
              direction: rtl;
              padding: 10px;
              background: white;
            }
            h1 {
              text-align: center;
              color: #1976d2;
              margin-bottom: 20px;
              font-size: 24px;
            }
            .products-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 10px;
              margin-top: 15px;
            }
            .product-card {
              border: 2px solid #e0e0e0;
              border-radius: 8px;
              padding: 10px;
              text-align: center;
              background: white;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            .product-image {
              width: 100%;
              max-width: 120px;
              height: 120px;
              object-fit: cover;
              border-radius: 4px;
              margin-bottom: 8px;
            }
            .product-name {
              font-weight: bold;
              font-size: 13px;
              margin: 5px 0;
              color: #333;
              line-height: 1.3;
              word-break: break-word;
            }
            .product-sku {
              color: #666;
              font-size: 11px;
              margin: 3px 0;
            }
            .product-quantity {
              background: #4caf50;
              color: white;
              padding: 6px 12px;
              border-radius: 15px;
              font-weight: bold;
              display: inline-block;
              margin-top: 8px;
              font-size: 13px;
            }
            .summary {
              text-align: center;
              margin-bottom: 15px;
              padding: 12px;
              background: #f5f5f5;
              border-radius: 8px;
              font-size: 13px;
            }
            .summary strong {
              color: #1976d2;
            }
            
            /* Print/Export buttons */
            .export-buttons {
              text-align: center;
              margin-bottom: 20px;
              display: flex;
              gap: 10px;
              justify-content: center;
              flex-wrap: wrap;
            }
            .export-btn {
              background: #1976d2;
              color: white;
              border: none;
              padding: 12px 24px;
              border-radius: 8px;
              font-size: 16px;
              font-weight: bold;
              cursor: pointer;
              font-family: 'Cairo', Arial, sans-serif;
              transition: background 0.3s ease;
              display: inline-flex;
              align-items: center;
              gap: 8px;
            }
            .export-btn:hover {
              background: #1565c0;
            }
            .export-btn.secondary {
              background: #4caf50;
            }
            .export-btn.secondary:hover {
              background: #45a049;
            }
            
            @media print {
              .export-buttons {
                display: none;
              }
            }
            
            /* Tablet and larger screens */
            @media (min-width: 600px) {
              body { padding: 20px; }
              h1 { font-size: 28px; margin-bottom: 30px; }
              .products-grid {
                grid-template-columns: repeat(3, 1fr);
                gap: 15px;
              }
              .product-card { padding: 15px; }
              .product-image {
                max-width: 140px;
                height: 140px;
              }
              .product-name { font-size: 15px; }
              .product-sku { font-size: 13px; }
              .product-quantity { font-size: 14px; }
              .summary { font-size: 15px; }
            }
            
            /* Desktop and print */
            @media (min-width: 900px) {
              .products-grid {
                grid-template-columns: repeat(4, 1fr);
                gap: 20px;
              }
              .product-image {
                max-width: 150px;
                height: 150px;
              }
              .product-name { font-size: 16px; }
              .product-sku { font-size: 14px; }
            }
            
            @media print {
              body { padding: 10px; }
              .products-grid { 
                grid-template-columns: repeat(4, 1fr);
                gap: 15px;
              }
              .product-card { 
                page-break-inside: avoid;
                border: 1px solid #ccc;
              }
            }
          </style>
        </head>
        <body>
          <h1>قائمة المنتجات - الطلبات المحددة</h1>
          
          <div class="export-buttons">
            <button class="export-btn" onclick="window.print()">
              🖨️ طباعة / حفظ PDF
            </button>
            <button class="export-btn secondary" onclick="downloadAsHTML()">
              💾 حفظ كملف HTML
            </button>
          </div>
          
          <div class="summary">
            <strong>عدد الطلبات:</strong> ${selectedOrdersForExport.length} | 
            <strong>عدد المنتجات بعد التجميع:</strong> ${products.length} |
            <strong>إجمالي الكمية:</strong> ${totalQuantity} | 
            <strong>التاريخ:</strong> ${new Date().toLocaleDateString('ar-IQ')}
          </div>
          <div class="products-grid">
            ${products.map(product => `
              <div class="product-card" data-group-key="${product.key}">
                ${product.image ? `<img src="${product.image}" alt="${product.name}" class="product-image" onerror="this.src='https://via.placeholder.com/150?text=No+Image'">` : '<div class="product-image" style="background: #f0f0f0; display: flex; align-items: center; justify-content: center;">لا توجد صورة</div>'}
                <div class="product-name">${product.name}</div>
                <div class="product-sku">SKU: ${product.sku || 'غير متوفر'}</div>
                <div class="product-sku">البراند: ${product.brand || 'غير متوفر'}</div>
                <div class="product-quantity">الكمية: ${product.quantity}</div>
              </div>
            `).join('')}
          </div>
          
          <script>
            function downloadAsHTML() {
              const blob = new Blob([document.documentElement.outerHTML], { type: 'text/html' });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'products_list_${new Date().toISOString().slice(0, 10)}.html';
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(url);
            }
          </script>
        </body>
        </html>
      `;

      // Open in new window for printing/saving
      const printWindow = window.open('', '_blank');
      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      setSuccess(`تم تجهيز ${products.length} منتج مجمع من ${selectedOrdersForExport.length} طلب`);
      setProductsListExportDialogOpen(false);
      
    } catch (error) {
      console.error('Error generating products list:', error);
      setError('حدث خطأ في إنشاء قائمة المنتجات: ' + error.message);
    } finally {
      setExportingProductsList(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusInfo = (status) => {
    return statusOptions.find(option => option.value === status) || statusOptions[0];
  };

  const toggleRowExpansion = (orderId) => {
    setExpandedRow(expandedRow === orderId ? null : orderId);
  };

  // Handle clear cache and refresh
  const handleClearCacheAndRefresh = () => {
    // Clear all caches
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
    
    // Clear local storage (optional, be careful with this)
    // localStorage.clear();
    
    // Clear session storage (optional)
    // sessionStorage.clear();
    
    // Force reload from server, bypassing cache
    window.location.reload(true);
  };

  if (!adminAuthService.isAuthenticated()) {
    navigate('/admin/login');
    return null;
  }

  return (
    <ThemeProvider theme={rtlTheme}>
      <div dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
        <Box sx={{ flexGrow: 1 }}>
      {/* Navigation Bar */}
      <AppBar position="static" sx={{ backgroundColor: '#4169E1' }}>
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={() => navigate('/admin/dashboard')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <OrdersIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            إدارة الطلبات
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleClearCacheAndRefresh}
            sx={{ 
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)'
              }
            }}
            title="تحديث ومسح الذاكرة"
          >
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container 
        maxWidth="xl" 
        sx={{ 
          mt: { xs: 1, sm: 2, md: 4 }, 
          mb: { xs: 1, sm: 2, md: 4 },
          px: { xs: 1, sm: 2, md: 3 }
        }}
      >
        {/* Page Header */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3, md: 4 } }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: { xs: 2, sm: 1 },
            mb: 1 
          }}>
            <Typography 
              variant={{ xs: 'h5', sm: 'h4' }} 
              sx={{ 
                fontWeight: 'bold', 
                color: '#4169E1',
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
              }}
            >
              إدارة الطلبات
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}>
              <Button
                variant="outlined"
                onClick={toggleAutoRefresh}
                sx={{ 
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 },
                  borderColor: autoRefresh ? '#4caf50' : '#9e9e9e',
                  color: autoRefresh ? '#4caf50' : '#9e9e9e',
                  '&:hover': {
                    borderColor: autoRefresh ? '#388e3c' : '#757575',
                    backgroundColor: autoRefresh ? 'rgba(76, 175, 80, 0.04)' : 'rgba(158, 158, 158, 0.04)'
                  }
                }}
                startIcon={!isSmallMobile && <span>{autoRefresh ? '🔔' : '🔕'}</span>}
              >
                {autoRefresh ? (isSmallMobile ? 'إيقاف التحديث' : 'إيقاف التحديث التلقائي') : (isSmallMobile ? 'تفعيل التحديث' : 'تفعيل التحديث التلقائي')}
              </Button>
              <Button
                variant="contained"
                onClick={() => setCreateOrderDialogOpen(true)}
                sx={{ 
                  backgroundColor: '#4169E1',
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 }
                }}
                startIcon={!isSmallMobile && <AddIcon />}
              >
                {isSmallMobile ? 'طلب جديد' : 'إنشاء طلب جديد'}
              </Button>
              <Button
                variant="contained"
                onClick={() => setExportDialogOpen(true)}
                sx={{ 
                  backgroundColor: '#4169E1',
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 }
                }}
                startIcon={!isSmallMobile && <ExportIcon />}
                disabled={selectedOrdersForExport.length === 0}
              >
                {isSmallMobile ? `تصدير (${selectedOrdersForExport.length})` : `تصدير الطلبات (${selectedOrdersForExport.length})`}
              </Button>
              <Button
                variant="contained"
                onClick={generateStockExport}
                sx={{ 
                  backgroundColor: '#10b981',
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 },
                  '&:hover': {
                    backgroundColor: '#059669'
                  }
                }}
                startIcon={!isSmallMobile && <ExportIcon />}
                disabled={selectedOrdersForExport.length === 0 || exportingPdf}
              >
                {exportingPdf ? 'جاري التصدير...' : (isSmallMobile ? 'مخزون' : `تحديث المخزون (${selectedOrdersForExport.length})`)}
              </Button>
              <Button
                variant="contained"
                onClick={generateProductsListExport}
                sx={{ 
                  backgroundColor: '#ff9800',
                  minWidth: { xs: '100%', sm: 'auto' },
                  fontSize: { xs: '0.875rem', sm: '0.875rem' },
                  px: { xs: 2, sm: 3 },
                  '&:hover': {
                    backgroundColor: '#f57c00'
                  }
                }}
                startIcon={!isSmallMobile && <ListAltIcon />}
                disabled={selectedOrdersForExport.length === 0 || exportingProductsList}
              >
                {exportingProductsList ? 'جاري التصدير...' : (isSmallMobile ? 'قائمة منتجات' : `تصدير قائمة المنتجات (${selectedOrdersForExport.length})`)}
              </Button>
            </Box>
          </Box>
          <Typography variant="body1" color="text.secondary">
            يمكنك من هنا عرض وإدارة جميع طلبات العملاء وتحديث حالاتها
            {autoRefresh && (
              <span style={{ color: '#4caf50', marginRight: '10px' }}>
                • التحديث التلقائي مفعّل (كل 30 ثانية)
              </span>
            )}
          </Typography>
        </Paper>

        {/* Stats Cards */}
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusFilter === '' ? '#e3f2fd' : 'white',
                border: statusFilter === '' ? '2px solid #4169E1' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: 4
                }
              }}
              onClick={() => handleStatusFilter('')}
            >
              <CardContent sx={{ 
                textAlign: 'center',
                p: { xs: 1, sm: 2 },
                '&:last-child': { pb: { xs: 1, sm: 2 } }
              }}>
                <OrdersIcon sx={{ 
                  fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                  color: '#4169E1', 
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stats.total}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2
                  }}
                >
                  إجمالي الطلبات
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusFilter === 'pending' ? '#fff3e0' : 'white',
                border: statusFilter === 'pending' ? '2px solid #ff9800' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-2px)',
                  boxShadow: isMobile ? 1 : 4
                }
              }}
              onClick={() => handleStatusFilter('pending')}
            >
              <CardContent sx={{ 
                textAlign: 'center',
                p: { xs: 1, sm: 2 },
                '&:last-child': { pb: { xs: 1, sm: 2 } }
              }}>
                <PendingIcon sx={{ 
                  fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                  color: '#ff9800', 
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stats.pending}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2
                  }}
                >
                  في الانتظار
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusFilter === 'confirmed' ? '#e3f2fd' : 'white',
                border: statusFilter === 'confirmed' ? '2px solid #2196f3' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-2px)',
                  boxShadow: isMobile ? 1 : 4
                }
              }}
              onClick={() => handleStatusFilter('confirmed')}
            >
              <CardContent sx={{ 
                textAlign: 'center',
                p: { xs: 1, sm: 2 },
                '&:last-child': { pb: { xs: 1, sm: 2 } }
              }}>
                <CheckIcon sx={{ 
                  fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                  color: '#2196f3', 
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stats.confirmed}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2
                  }}
                >
                  مؤكدة
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusFilter === 'shipped' ? '#f3e5f5' : 'white',
                border: statusFilter === 'shipped' ? '2px solid #9c27b0' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-2px)',
                  boxShadow: isMobile ? 1 : 4
                }
              }}
              onClick={() => handleStatusFilter('shipped')}
            >
              <CardContent sx={{ 
                textAlign: 'center',
                p: { xs: 1, sm: 2 },
                '&:last-child': { pb: { xs: 1, sm: 2 } }
              }}>
                <ShippingIcon sx={{ 
                  fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                  color: '#9c27b0', 
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stats.shipped}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2
                  }}
                >
                  تم الشحن
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4} md={2.4}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusFilter === 'delivered' ? '#e8f5e8' : 'white',
                border: statusFilter === 'delivered' ? '2px solid #4caf50' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-2px)',
                  boxShadow: isMobile ? 1 : 4
                }
              }}
              onClick={() => handleStatusFilter('delivered')}
            >
              <CardContent sx={{ 
                textAlign: 'center',
                p: { xs: 1, sm: 2 },
                '&:last-child': { pb: { xs: 1, sm: 2 } }
              }}>
                <CheckIcon sx={{ 
                  fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                  color: '#4caf50', 
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stats.delivered}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2
                  }}
                >
                  تم التوصيل
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {/* Second row for Cancelled and Revenue */}
        <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ mb: { xs: 2, sm: 3, md: 4 } }}>
          <Grid item xs={6} sm={6} md={6}>
            <Card 
              sx={{ 
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                backgroundColor: statusFilter === 'cancelled' ? '#ffebee' : 'white',
                border: statusFilter === 'cancelled' ? '2px solid #f44336' : '1px solid #e0e0e0',
                '&:hover': {
                  transform: isMobile ? 'none' : 'translateY(-2px)',
                  boxShadow: isMobile ? 1 : 4
                }
              }}
              onClick={() => handleStatusFilter('cancelled')}
            >
              <CardContent sx={{ 
                textAlign: 'center',
                p: { xs: 1, sm: 2 },
                '&:last-child': { pb: { xs: 1, sm: 2 } }
              }}>
                <CancelIcon sx={{ 
                  fontSize: { xs: '24px', sm: '32px', md: '40px' }, 
                  color: '#f44336', 
                  mb: { xs: 0.5, sm: 1 }
                }} />
                <Typography 
                  variant={{ xs: 'h6', sm: 'h5', md: 'h4' }} 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }
                  }}
                >
                  {stats.cancelled}
                </Typography>
                <Typography 
                  color="text.secondary"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem' },
                    lineHeight: 1.2
                  }}
                >
                  ملغية
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        {/* Search and Filters */}
        <Paper sx={{ p: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={12} md={4}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="البحث في الطلبات (رقم الطلب، كود الطلب، اسم العميل، البريد الإلكتروني، رقم الهاتف)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                size={isMobile ? "small" : "medium"}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.5}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>تصفية حسب الحالة</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="تصفية حسب الحالة"
                >
                  <MenuItem value="">كل الحالات</MenuItem>
                  {statusOptions.map((status) => (
                    <MenuItem key={status.value} value={status.value}>
                      {status.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>تصفية حسب التاريخ</InputLabel>
                <Select
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  label="تصفية حسب التاريخ"
                >
                  <MenuItem value="">كل التواريخ</MenuItem>
                  <MenuItem value="today">اليوم</MenuItem>
                  <MenuItem value="yesterday">أمس</MenuItem>
                  <MenuItem value="week">آخر أسبوع</MenuItem>
                  <MenuItem value="month">آخر شهر</MenuItem>
                  <MenuItem value="custom">تاريخ مخصص</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>نوع الطلب</InputLabel>
                <Select
                  value={wholesaleFilter}
                  onChange={(e) => setWholesaleFilter(e.target.value)}
                  label="نوع الطلب"
                >
                  <MenuItem value="">كل الطلبات</MenuItem>
                  <MenuItem value="wholesale">جملة</MenuItem>
                  <MenuItem value="retail">تجزئة</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>تصفية حسب موعد التوصيل</InputLabel>
                <Select
                  value={deliveryDateFilter}
                  onChange={(e) => setDeliveryDateFilter(e.target.value)}
                  label="تصفية حسب موعد التوصيل"
                >
                  <MenuItem value="">كل المواعيد</MenuItem>
                  <MenuItem value="today">اليوم</MenuItem>
                  <MenuItem value="tomorrow">غداً</MenuItem>
                  <MenuItem value="yesterday">أمس</MenuItem>
                  <MenuItem value="custom">تاريخ مخصص</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Custom date range fields */}
            {dateFilter === 'custom' && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="من تاريخ"
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="إلى تاريخ"
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </>
            )}
            
            {/* Custom delivery date field */}
            {deliveryDateFilter === 'custom' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="موعد التوصيل"
                  value={customDeliveryDate}
                  onChange={(e) => setCustomDeliveryDate(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Orders Table/Cards */}
        <Paper>
          {/* Mobile Card View */}
          {isMobile ? (
            <Box sx={{ p: 1 }}>
              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>جاري التحميل...</Typography>
                </Box>
              ) : filteredOrders.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography>لا توجد طلبات</Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, py: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectAllOrders}
                          onChange={handleSelectAllOrdersForExport}
                          indeterminate={selectedOrdersForExport.length > 0 && selectedOrdersForExport.length < filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                        />
                      }
                      label="تحديد الكل"
                      sx={{ fontSize: '0.875rem' }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length} طلب
                    </Typography>
                  </Box>
                  
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order) => {
                      const isSelected = selectedOrdersForExport.includes(order.id);
                      return (
                        <Card 
                          key={order.id} 
                          sx={{ 
                            border: isSelected ? '2px solid #4169E1' : '1px solid #e0e0e0',
                            backgroundColor: isSelected ? '#f8f9ff' : 'white'
                          }}
                        >
                          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                            {/* Card Header */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Checkbox
                                  checked={isSelected}
                                  onChange={(e) => handleSelectOrderForExport(order.id, e.target.checked)}
                                  size="small"
                                />
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4169E1' }}>
                                  #{order.id}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrderDetails(order)}
                                  sx={{ color: '#4169E1' }}
                                >
                                  <ViewIcon />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveToWholesaleTraders(order)}
                                  disabled={savingToWholesale === order.id}
                                  sx={{ 
                                    color: '#059669',
                                    '&:disabled': {
                                      opacity: 0.6
                                    }
                                  }}
                                  title="إضافة إلى تجار الجملة"
                                >
                                  <PersonAddIcon />
                                </IconButton>
                              </Box>
                            </Box>
                            
                            {/* Order Type and Status */}
                            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                              {order.wholesale === 1 || order.wholesale === '1' ? (
                                <Chip
                                  label="جملة"
                                  size="small"
                                  sx={{
                                    backgroundColor: '#2E7D32',
                                    color: 'white',
                                    fontWeight: 'bold'
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="تجزئة"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: '#4169E1',
                                    color: '#4169E1'
                                  }}
                                />
                              )}
                              
                              <FormControl size="small" sx={{ minWidth: 100 }}>
                                <Select
                                  value={order.status}
                                  onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                  displayEmpty
                                  sx={{ 
                                    fontSize: '0.75rem',
                                    '& .MuiSelect-select': {
                                      paddingY: '2px',
                                      paddingX: '6px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }
                                  }}
                                  renderValue={(selected) => {
                                    const selectedStatus = statusOptions.find(s => s.value === selected);
                                    return selectedStatus ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {React.cloneElement(selectedStatus.icon, { 
                                          sx: { fontSize: '14px', mr: 0.5 } 
                                        })}
                                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>
                                          {selectedStatus.label}
                                        </Typography>
                                      </Box>
                                    ) : selected;
                                  }}
                                >
                                  {statusOptions.map((status) => (
                                    <MenuItem key={status.value} value={status.value}>
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {React.cloneElement(status.icon, { 
                                          sx: { fontSize: '14px', mr: 1 } 
                                        })}
                                        <Typography sx={{ fontSize: '0.8rem' }}>
                                          {status.label}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Box>
                            
                            {/* Customer Info */}
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {order.customer_name || 'غير محدد'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                                {order.customer_email || 'غير محدد'}
                              </Typography>
                              {order.customer_phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                                    {order.customer_phone}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleWhatsAppClick(order.customer_phone)}
                                    sx={{ 
                                      color: '#25D366', 
                                      backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                      '&:hover': { 
                                        backgroundColor: 'rgba(37, 211, 102, 0.2)' 
                                      },
                                      p: 0.5
                                    }}
                                  >
                                    <PhoneIcon sx={{ fontSize: '16px' }} />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>
                            
                            {/* Amount and Date */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4169E1' }}>
                                  {parseFloat(order.after_discount_amount || order.total_amount).toLocaleString()} د.ع
                                </Typography>
                                {order.after_discount_amount && parseFloat(order.after_discount_amount) !== parseFloat(order.total_amount) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textDecoration: 'line-through' }}>
                                    {parseFloat(order.total_amount).toLocaleString()} د.ع
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {new Date(order.created_at).toLocaleDateString('ar-EG')}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })
                  }
                </Stack>
              )}
            </Box>
          ) : (
            /* Desktop Table View */
            <TableContainer 
              sx={{
                borderRadius: '12px',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                border: '1px solid #e0e7ff',
                overflow: 'hidden',
                backgroundColor: 'white'
              }}
            >
              <Table sx={{ minWidth: 900 }}>
              <TableHead sx={{ backgroundColor: '#f8fafc' }}>
                <TableRow>
                  <TableCell 
                    padding="checkbox"
                    sx={{ 
                      backgroundColor: '#f8fafc',
                      fontWeight: 700,
                      borderBottom: '2px solid #e5e7eb'
                    }}
                  >
                    <Checkbox
                      checked={selectAllOrders}
                      onChange={handleSelectAllOrdersForExport}
                      indeterminate={selectedOrdersForExport.length > 0 && selectedOrdersForExport.length < filteredOrders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).length}
                      sx={{ color: '#4169E1' }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>رقم الطلب</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>النوع</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>العميل</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>المبلغ الإجمالي</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>الحالة</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>تاريخ الطلب</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#374151' }}>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell 
                      colSpan={8} 
                      align="center"
                      sx={{
                        padding: '60px 20px',
                        backgroundColor: '#fafbfc'
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <Box sx={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          border: '4px solid #e0e7ff',
                          borderTop: '4px solid #4169E1',
                          animation: 'spin 1s linear infinite',
                          '@keyframes spin': {
                            '0%': { transform: 'rotate(0deg)' },
                            '100%': { transform: 'rotate(360deg)' }
                          }
                        }} />
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: '#6b7280',
                            fontSize: '1rem',
                            fontWeight: 500
                          }}
                        >
                          جاري تحميل الطلبات...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell 
                      colSpan={8} 
                      align="center"
                      sx={{
                        padding: '60px 20px',
                        backgroundColor: '#fafbfc'
                      }}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center',
                        gap: 2
                      }}>
                        <Box sx={{
                          width: '60px',
                          height: '60px',
                          borderRadius: '50%',
                          backgroundColor: '#f3f4f6',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 1
                        }}>
                          <OrdersIcon sx={{ fontSize: '30px', color: '#9ca3af' }} />
                        </Box>
                        <Typography 
                          variant="h6" 
                          sx={{ 
                            color: '#374151',
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            marginBottom: 0.5
                          }}
                        >
                          لا توجد طلبات
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#6b7280',
                            fontSize: '0.9rem',
                            textAlign: 'center'
                          }}
                        >
                          لم يتم العثور على أي طلبات تطابق معايير البحث الحالية
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((order) => {
                      const isSelected = selectedOrdersForExport.includes(order.id);
                      return (
                        <React.Fragment key={order.id}>
                          <TableRow 
                            sx={{
                              '&:hover': {
                                backgroundColor: '#f8fafc',
                                transform: 'scale(1.002)',
                                transition: 'all 0.2s ease',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                              },
                              cursor: 'pointer',
                              borderLeft: isSelected ? '4px solid #4169E1' : 'none'
                            }}
                          >
                            <TableCell 
                              padding="checkbox"
                              sx={{
                                borderBottom: '1px solid #f1f5f9',
                                padding: '12px'
                              }}
                            >
                              <Checkbox
                                checked={isSelected}
                                onChange={(e) => handleSelectOrderForExport(order.id, e.target.checked)}
                                sx={{ 
                                  color: '#4169E1',
                                  '&.Mui-checked': {
                                    color: '#4169E1'
                                  }
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                              <Button
                                variant="text"
                                onClick={() => toggleRowExpansion(order.id)}
                                startIcon={expandedRow === order.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                sx={{
                                  color: '#4169E1',
                                  fontWeight: 600,
                                  fontSize: '0.875rem',
                                  '&:hover': {
                                    backgroundColor: '#e0e7ff',
                                    color: '#3730a3'
                                  },
                                  borderRadius: '8px',
                                  padding: '6px 12px'
                                }}
                              >
                                #{order.id}
                              </Button>
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                              {order.wholesale === 1 || order.wholesale === '1' ? (
                                <Chip
                                  label="جملة"
                                  size="small"
                                  sx={{
                                    backgroundColor: '#059669',
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                    height: '24px',
                                    boxShadow: '0 2px 4px rgba(5, 150, 105, 0.2)'
                                  }}
                                />
                              ) : (
                                <Chip
                                  label="تجزئة"
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    borderColor: '#4169E1',
                                    color: '#4169E1',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    borderRadius: '6px',
                                    height: '24px',
                                    backgroundColor: '#eff6ff',
                                    borderWidth: '1.5px'
                                  }}
                                />
                              )}
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                              <Box>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    fontWeight: 600, 
                                    color: '#111827',
                                    fontSize: '0.875rem',
                                    lineHeight: 1.4
                                  }}
                                >
                                  {order.customer_name || 'غير محدد'}
                                </Typography>
                                <Typography 
                                  variant="caption" 
                                  sx={{ 
                                    color: '#6b7280',
                                    fontSize: '0.75rem',
                                    display: 'block',
                                    marginTop: '2px'
                                  }}
                                >
                                  {order.customer_email || 'غير محدد'}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography 
                                  variant="subtitle1" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: '#059669',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.4
                                  }}
                                >
                                  {parseFloat(order.after_discount_amount || order.total_amount).toLocaleString()} د.ع
                                </Typography>
                                {order.after_discount_amount && parseFloat(order.after_discount_amount) !== parseFloat(order.total_amount) && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#9ca3af',
                                      textDecoration: 'line-through',
                                      fontSize: '0.75rem',
                                      display: 'block',
                                      marginTop: '2px'
                                    }}
                                  >
                                    {parseFloat(order.total_amount).toLocaleString()} د.ع
                                  </Typography>
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                              <FormControl size="small" sx={{ minWidth: 130 }}>
                                <Select
                                  value={order.status}
                                  onChange={(e) => handleStatusChange(order.id, e.target.value)}
                                  displayEmpty
                                  sx={{ 
                                    fontSize: '0.875rem',
                                    borderRadius: '8px',
                                    backgroundColor: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      border: 'none'
                                    },
                                    '&:hover': {
                                      backgroundColor: '#f1f5f9',
                                      border: '1px solid #cbd5e1'
                                    },
                                    '&.Mui-focused': {
                                      backgroundColor: 'white',
                                      boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.1)'
                                    },
                                    '& .MuiSelect-select': {
                                      paddingY: '8px',
                                      paddingX: '12px',
                                      display: 'flex',
                                      alignItems: 'center'
                                    }
                                  }}
                                  renderValue={(selected) => {
                                    const selectedStatus = statusOptions.find(s => s.value === selected);
                                    return selectedStatus ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {React.cloneElement(selectedStatus.icon, { 
                                          sx: { fontSize: '16px', mr: 1, color: getStatusColor(selectedStatus.color) } 
                                        })}
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            color: '#374151'
                                          }}
                                        >
                                          {selectedStatus.label}
                                        </Typography>
                                      </Box>
                                    ) : selected;
                                  }}
                                >
                                  {statusOptions.map((status) => (
                                    <MenuItem 
                                      key={status.value} 
                                      value={status.value}
                                      sx={{
                                        '&:hover': {
                                          backgroundColor: '#f8fafc'
                                        }
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {React.cloneElement(status.icon, { 
                                          sx: { fontSize: '16px', mr: 1.5, color: getStatusColor(status.color) } 
                                        })}
                                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
                                          {status.label}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell sx={{ padding: '12px', borderBottom: '1px solid #f1f5f9' }}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: '#6b7280',
                                  fontSize: '0.875rem',
                                  fontWeight: 500,
                                  backgroundColor: '#f9fafb',
                                  padding: '4px 8px',
                                  borderRadius: '6px',
                                  display: 'inline-block',
                                  border: '1px solid #e5e7eb'
                                }}
                              >
                                {new Date(order.created_at).toLocaleDateString('ar-EG')}
                              </Typography>
                            </TableCell>
                            <TableCell 
                              align="center" 
                              sx={{ 
                                padding: '12px', 
                                borderBottom: '1px solid #f1f5f9' 
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                gap: 1, 
                                justifyContent: 'center',
                                alignItems: 'center'
                              }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewOrderDetails(order)}
                                  title="عرض التفاصيل"
                                  sx={{ 
                                    color: '#4169E1',
                                    backgroundColor: '#eff6ff',
                                    border: '1px solid #bfdbfe',
                                    borderRadius: '8px',
                                    padding: '6px',
                                    '&:hover': {
                                      backgroundColor: '#dbeafe',
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.2)'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <ViewIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleSaveToWholesaleTraders(order)}
                                  title="إضافة إلى تجار الجملة"
                                  disabled={savingToWholesale === order.id}
                                  sx={{ 
                                    color: '#059669',
                                    backgroundColor: '#d1fae5',
                                    border: '1px solid #6ee7b7',
                                    borderRadius: '8px',
                                    padding: '6px',
                                    '&:hover': {
                                      backgroundColor: '#a7f3d0',
                                      transform: 'scale(1.05)',
                                      boxShadow: '0 2px 8px rgba(5, 150, 105, 0.2)'
                                    },
                                    '&:disabled': {
                                      opacity: 0.6,
                                      cursor: 'not-allowed'
                                    },
                                    transition: 'all 0.2s ease'
                                  }}
                                >
                                  <PersonAddIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </Box>
                            </TableCell>
                          </TableRow>
                          
                          {/* Expandable Row */}
                          <TableRow>
                            <TableCell 
                              style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} 
                              colSpan={8}
                            >
                              <Collapse in={expandedRow === order.id} timeout="auto" unmountOnExit>
                                <Box sx={{ 
                                  margin: 2,
                                  padding: 3,
                                  backgroundColor: '#f8fafc',
                                  borderRadius: '12px',
                                  border: '1px solid #e2e8f0',
                                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                                }}>
                                  <Typography 
                                    variant="h6" 
                                    gutterBottom 
                                    component="div"
                                    sx={{
                                      color: '#1f2937',
                                      fontWeight: 700,
                                      fontSize: '1.1rem',
                                      marginBottom: 2
                                    }}
                                  >
                                    معلومات إضافية
                                  </Typography>
                                  <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <Typography variant="body2" component="span"><strong>رقم الهاتف:</strong></Typography>
                                        {order.customer_phone ? (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="body2" component="span">{order.customer_phone}</Typography>
                                            <IconButton
                                              size="small"
                                              onClick={() => handleWhatsAppClick(order.customer_phone)}
                                              sx={{ 
                                                color: '#25D366', 
                                                backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                                '&:hover': { 
                                                  backgroundColor: 'rgba(37, 211, 102, 0.2)' 
                                                }
                                              }}
                                              title="إرسال رسالة واتساب"
                                            >
                                              <PhoneIcon fontSize="small" />
                                            </IconButton>
                                          </Box>
                                        ) : (
                                          <Typography variant="body2" component="span">غير محدد</Typography>
                                        )}
                                      </Box>
                                      <Typography variant="body2">
                                        <strong>العنوان:</strong> {order.shipping_address || 'غير محدد'}
                                      </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Typography variant="body2">
                                        <strong>طريقة الدفع:</strong> {order.payment_method || 'غير محدد'}
                                      </Typography>
                                      <Typography variant="body2">
                                        <strong>ملاحظات:</strong> {order.notes || 'لا توجد ملاحظات'}
                                      </Typography>
                                    </Grid>
                                  </Grid>
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })
                )}
              </TableBody>
              </Table>
            </TableContainer>
          )}
          
          <TablePagination
            component="div"
            count={filteredOrders.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={isMobile ? "الصفوف:" : "عدد الصفوف في الصفحة:"}
            labelDisplayedRows={({ from, to, count }) => 
              isMobile ? `${from}-${to}/${count}` : `${from}-${to} من أصل ${count !== -1 ? count : `أكثر من ${to}`}`
            }
            sx={{
              backgroundColor: '#f8fafc',
              borderTop: '1px solid #e2e8f0',
              borderRadius: '0 0 12px 12px',
              '& .MuiTablePagination-toolbar': {
                px: { xs: 2, sm: 3 },
                minHeight: { xs: 52, sm: 56 },
                backgroundColor: '#f8fafc'
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
                fontWeight: 500,
                color: '#374151'
              },
              '& .MuiTablePagination-select': {
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '0.875rem'
              },
              '& .MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  backgroundColor: 'white',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  margin: '0 2px',
                  '&:hover': {
                    backgroundColor: '#f3f4f6',
                    borderColor: '#9ca3af'
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#f9fafb',
                    borderColor: '#e5e7eb',
                    color: '#d1d5db'
                  }
                }
              }
            }}
          />
        </Paper>
      </Container>

      {/* Order Details Dialog */}
      <Dialog 
        open={detailsDialogOpen} 
        onClose={() => setDetailsDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { minHeight: isMobile ? '100vh' : '80vh' }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          px: { xs: 2, sm: 3 },
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          zIndex: 1
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 }
          }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.1rem', sm: '1.5rem' },
                  whiteSpace: { xs: 'normal', sm: 'nowrap' },
                  lineHeight: 1.3
                }}
              >
                تفاصيل الطلب #{selectedOrder?.id}
              </Typography>
              {selectedOrder && isMobile && (
                <Chip
                  icon={getStatusInfo(selectedOrder.status).icon}
                  label={getStatusInfo(selectedOrder.status).label}
                  color={getStatusInfo(selectedOrder.status).color}
                  size="small"
                  sx={{ 
                    fontSize: '0.7rem',
                    height: 24,
                    mt: 0.5
                  }}
                />
              )}
            </Box>
            {selectedOrder && !isMobile && (
              <Chip
                icon={getStatusInfo(selectedOrder.status).icon}
                label={getStatusInfo(selectedOrder.status).label}
                color={getStatusInfo(selectedOrder.status).color}
                size="large"
                sx={{ 
                  fontSize: '0.9rem', 
                  px: 2
                }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: { xs: 1.5, sm: 2, md: 3 },
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#c0c0c0',
            borderRadius: '4px'
          }
        }}>
          {/* Debug info - remove this in production */}
          {console.log('=== Dialog Render Debug ===', {
            selectedOrderTotal: selectedOrder?.total_amount,
            selectedOrderAfterDiscount: selectedOrder?.after_discount_amount,
            editedOrderTotal: editedOrder?.total_amount,
            editedOrderAfterDiscount: editedOrder?.after_discount_amount,
            hasChanges: hasChanges
          })}
          
          {/* Success/Error Messages within Dialog */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          
          {selectedOrder && (
            <Box>
              {/* Section 1: Order Details */}
              <Paper sx={{ 
                p: { xs: 1.5, sm: 3 }, 
                mb: { xs: 1.5, sm: 3 }, 
                backgroundColor: '#f8f9fa',
                boxShadow: { xs: '0 1px 3px rgba(0,0,0,0.1)', sm: '0 2px 4px rgba(0,0,0,0.1)' }
              }}>
                <Typography 
                  sx={{ 
                    mb: { xs: 1.5, sm: 2 },
                    color: '#4169E1', 
                    fontWeight: 'bold',
                    fontSize: { xs: '1rem', sm: '1.25rem' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <span style={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>📋</span>
                  معلومات الطلب
                </Typography>
                
                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: { xs: 2, md: 2 } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary" 
                        sx={{ 
                          fontSize: { xs: '0.9rem', sm: '0.875rem' },
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        معلومات العميل
                      </Typography>
                      <Box sx={{ 
                        p: { xs: 1.5, sm: 2 }, 
                        backgroundColor: 'white', 
                        borderRadius: 2, 
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>الاسم:</strong> {selectedOrder.customer_name || 'غير محدد'}
                        </Typography>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.8rem', sm: '1rem' },
                          wordBreak: 'break-word',
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>البريد:</strong> {selectedOrder.customer_email || 'غير محدد'}
                        </Typography>
                        <Box sx={{ mb: { xs: 1, sm: 1.5 } }}>
                          <Typography component="div" sx={{ 
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            lineHeight: 1.5
                          }}>
                            <strong style={{ color: '#666' }}>رقم الهاتف:</strong>
                          </Typography>
                          {selectedOrder.customer_phone ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                              <Typography component="span" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>
                                {selectedOrder.customer_phone}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleWhatsAppClick(selectedOrder.customer_phone)}
                                sx={{ 
                                  color: '#25D366', 
                                  backgroundColor: 'rgba(37, 211, 102, 0.1)',
                                  p: { xs: 0.5, sm: 1 },
                                  minWidth: { xs: 32, sm: 36 },
                                  height: { xs: 32, sm: 36 },
                                  '&:hover': { 
                                    backgroundColor: 'rgba(37, 211, 102, 0.2)' 
                                  }
                                }}
                                title="إرسال رسالة واتساب"
                              >
                                <PhoneIcon sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }} />
                              </IconButton>
                            </Box>
                          ) : (
                            <Typography component="span" sx={{ fontSize: { xs: '0.85rem', sm: '1rem' } }}>غير محدد</Typography>
                          )}
                        </Box>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>المدينة:</strong> {selectedOrder.city || 'غير محدد'}
                        </Typography>
                        <Typography sx={{ 
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>العنوان:</strong> {selectedOrder.shipping_address || 'غير محدد'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Box sx={{ mb: { xs: 2, md: 2 } }}>
                      <Typography 
                        variant="subtitle2" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: { xs: '0.9rem', sm: '0.875rem' },
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        تفاصيل الطلب
                      </Typography>
                      <Box sx={{ 
                        p: { xs: 1.5, sm: 2 }, 
                        backgroundColor: 'white', 
                        borderRadius: 2, 
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)'
                      }}>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>رقم الطلب:</strong> {selectedOrder.order_code || selectedOrder.id}
                        </Typography>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.75rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>تاريخ الطلب:</strong> {new Date(selectedOrder.created_at).toLocaleString('ar-EG')}
                        </Typography>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>طريقة الدفع:</strong> {selectedOrder.payment_method || 'غير محدد'}
                        </Typography>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5,
                          color: '#ff9800'
                        }}>
                          <strong style={{ color: '#666' }}>رسوم التوصيل:</strong> {parseFloat((editedOrder && editedOrder.delivery) || selectedOrder.delivery || 0).toLocaleString()} د.ع
                        </Typography>
                        <Box sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}>
                          <Typography sx={{
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            lineHeight: 1.5,
                            color: '#f44336'
                          }}>
                            <strong style={{ color: '#666' }}>الخصم:</strong>
                          </Typography>
                          <TextField
                            type="number"
                            value={editedOrder?.discount_amount || selectedOrder.discount_amount || 0}
                            onChange={(e) => handleUpdateDiscount(e.target.value)}
                            size="small"
                            sx={{
                              width: '120px',
                              '& input': {
                                textAlign: 'center',
                                fontSize: '0.9rem',
                                padding: '4px 8px'
                              }
                            }}
                            InputProps={{
                              endAdornment: <span style={{ fontSize: '0.85rem', color: '#666' }}>د.ع</span>
                            }}
                          />
                        </Box>
                        <Typography sx={{ 
                          mb: { xs: 1, sm: 1.5 },
                          fontSize: { xs: '0.85rem', sm: '1rem' },
                          lineHeight: 1.5
                        }}>
                          <strong style={{ color: '#666' }}>كود الكوبون:</strong> {(editedOrder && editedOrder.coupon_code) || selectedOrder.coupon_code || 'لا يوجد'}
                        </Typography>
                        <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography component="span" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                            <strong>نوع الطلب:</strong>
                          </Typography>
                          {((editedOrder && editedOrder.wholesale) || selectedOrder.wholesale) === 1 || ((editedOrder && editedOrder.wholesale) || selectedOrder.wholesale) === '1' ? (
                            <Chip label="طلب جملة" color="info" size="small" />
                          ) : (
                            <Chip label="طلب مفرد" color="primary" size="small" />
                          )}
                        </Box>
                        <Box sx={{ 
                          pt: { xs: 1, sm: 1 },
                          mt: { xs: 1, sm: 1 },
                          borderTop: '2px solid #e0e0e0',
                          backgroundColor: { xs: '#f0f7ff', sm: 'transparent' },
                          p: { xs: 1, sm: 0 },
                          borderRadius: { xs: 1, sm: 0 },
                          mx: { xs: -1.5, sm: 0 }
                        }}>
                          <Typography sx={{ 
                            fontWeight: 'bold', 
                            fontSize: { xs: '1rem', sm: '1.1rem' }, 
                            color: '#4169E1',
                            lineHeight: 1.5
                          }}>
                            <strong>المبلغ النهائي:</strong> 
                            <span style={{ 
                              fontSize: isMobile ? '1.1rem' : '1.2rem',
                              marginRight: '4px'
                            }}>
                              {parseFloat((editedOrder && editedOrder.after_discount_amount) || (editedOrder && editedOrder.total_amount) || selectedOrder.after_discount_amount || selectedOrder.total_amount).toLocaleString()} د.ع
                            </span>
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                {/* Receipt Section for Wholesale Orders */}
                {(selectedOrder.wholesale === 1 || selectedOrder.wholesale === '1') && selectedOrder.receipt && (
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon fontSize="small" />
                      <Typography variant="subtitle2" color="text.secondary" component="span">
                        إيصال الدفع
                      </Typography>
                    </Box>
                    <Box sx={{ mt: 1, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Box
                          component="img"
                          src={selectedOrder.receipt.startsWith('http') ? 
                            selectedOrder.receipt : 
                            `${API_BASE_URL}/${selectedOrder.receipt}`
                          }
                          alt="إيصال الدفع"
                          sx={{ 
                            width: 120, 
                            height: 120, 
                            borderRadius: 2, 
                            objectFit: 'cover',
                            border: '2px solid #e0e0e0',
                            cursor: 'pointer',
                            transition: 'transform 0.2s ease',
                            '&:hover': {
                              transform: 'scale(1.05)',
                              border: '2px solid #4169E1'
                            }
                          }}
                          onClick={() => {
                            const imageUrl = selectedOrder.receipt.startsWith('http') ? 
                              selectedOrder.receipt : 
                              `${API_BASE_URL}/${selectedOrder.receipt}`;
                            window.open(imageUrl, '_blank');
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                        <Box 
                          sx={{ 
                            width: 120, 
                            height: 120, 
                            borderRadius: 2, 
                            border: '2px dashed #ccc',
                            display: 'none',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexDirection: 'column',
                            color: '#999',
                            backgroundColor: '#f9f9f9'
                          }}
                        >
                          <ImageIcon sx={{ fontSize: 32, mb: 1 }} />
                          <Typography variant="caption">لا يمكن تحميل الصورة</Typography>
                        </Box>
                        
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                            <strong>اسم الملف:</strong> {selectedOrder.receipt.split('/').pop()}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                            انقر على الصورة لعرضها بحجم كامل في نافذة جديدة
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ImageIcon />}
                            onClick={() => {
                              const imageUrl = selectedOrder.receipt.startsWith('http') ? 
                                selectedOrder.receipt : 
                                `${API_BASE_URL}/${selectedOrder.receipt}`;
                              window.open(imageUrl, '_blank');
                            }}
                            sx={{ mr: 1 }}
                          >
                            عرض كامل
                          </Button>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<ExportIcon />}
                            onClick={() => {
                              const imageUrl = selectedOrder.receipt.startsWith('http') ? 
                                selectedOrder.receipt : 
                                `${API_BASE_URL}/${selectedOrder.receipt}`;
                              const link = document.createElement('a');
                              link.href = imageUrl;
                              link.download = selectedOrder.receipt.split('/').pop();
                              link.click();
                            }}
                            color="secondary"
                          >
                            تحميل
                          </Button>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}
                
                {selectedOrder.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">ملاحظات العميل</Typography>
                    <Box sx={{ mt: 1, p: 2, backgroundColor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                      <Typography>{selectedOrder.notes}</Typography>
                    </Box>
                  </Box>
                )}
                
                {/* Admin Notes Section with Edit/Save functionality */}
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: 1,
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                    <Typography 
                      variant="subtitle2" 
                      sx={{ 
                        fontWeight: 600, 
                        color: '#4169E1',
                        fontSize: { xs: '0.9rem', sm: '1rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                      }}
                    >
                      <span>📝</span> ملاحظات الإدارة
                    </Typography>
                    {!isEditingAdminNotes ? (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setIsEditingAdminNotes(true)}
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          py: { xs: 0.5, sm: 1 },
                          px: { xs: 1, sm: 2 }
                        }}
                      >
                        تعديل
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            setIsEditingAdminNotes(false);
                            setAdminNotesValue(selectedOrder.admin_notes || '');
                          }}
                          disabled={savingAdminNotes}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.5, sm: 1 }
                          }}
                        >
                          إلغاء
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveAdminNotes}
                          disabled={savingAdminNotes}
                          sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            py: { xs: 0.5, sm: 1 }
                          }}
                        >
                          {savingAdminNotes ? 'جاري الحفظ...' : 'حفظ'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  <Box sx={{ 
                    mt: 1, 
                    p: { xs: 1.5, sm: 2 }, 
                    backgroundColor: isEditingAdminNotes ? '#f0f7ff' : '#fff9e6', 
                    borderRadius: 1, 
                    border: isEditingAdminNotes ? '2px solid #4169E1' : '1px solid #ffd54f',
                    minHeight: 80
                  }}>
                    {isEditingAdminNotes ? (
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={adminNotesValue}
                        onChange={(e) => setAdminNotesValue(e.target.value)}
                        placeholder="أضف ملاحظات داخلية للإدارة فقط..."
                        variant="outlined"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            fontSize: { xs: '0.85rem', sm: '1rem' }
                          }
                        }}
                      />
                    ) : (
                      <Typography sx={{ 
                        whiteSpace: 'pre-wrap',
                        fontSize: { xs: '0.85rem', sm: '1rem' },
                        color: adminNotesValue ? 'inherit' : '#999',
                        fontStyle: adminNotesValue ? 'normal' : 'italic'
                      }}>
                        {adminNotesValue || 'لا توجد ملاحظات إدارية'}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>

              {/* Section 2: Order Items Table */}
              <Paper sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 2,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#4169E1', 
                      fontWeight: 'bold',
                      fontSize: { xs: '1.1rem', sm: '1.25rem' }
                    }}
                  >
                    🛍️ عناصر الطلب
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={handleOpenAddProductDialog}
                    sx={{ 
                      backgroundColor: '#4caf50',
                      width: { xs: '100%', sm: 'auto' }
                    }}
                    startIcon={<AddIcon />}
                  >
                    إضافة منتج
                  </Button>
                </Box>
                
                {editedOrder && editedOrder.items && editedOrder.items.length > 0 ? (
                  <>
                  {/* Desktop Table View */}
                  <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>المنتج</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">الكمية</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">السعر</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">الإجمالي</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">الحالة</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">الإجراءات</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {editedOrder.items.map((item, index) => (
                          <TableRow key={index} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#fafafa' } }}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                {item.product_image && (
                                  <Box
                                    component="img"
                                    src={item.product_image.startsWith('http') ? 
                                      item.product_image : 
                                      `${API_BASE_URL}/api/uploads/products/${item.product_image}`
                                    }
                                    alt={item.product_name}
                                    sx={{ 
                                      width: 50, 
                                      height: 50, 
                                      borderRadius: 1, 
                                      mr: 2,
                                      objectFit: 'cover'
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                    }}
                                  />
                                )}
                                <Box>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                    {item.product_name}
                                  </Typography>
                                  {item.product_sku && (
                                    <Typography variant="caption" color="text.secondary">
                                      كود المنتج: {item.product_sku}
                                    </Typography>
                                  )}
                                  {item.brand_name && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                      البراند: {item.brand_name}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleUpdateItemQuantity(index, parseInt(e.target.value) || 0)}
                                size="small"
                                sx={{ width: 80 }}
                                inputProps={{ min: 0 }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                type="number"
                                value={item.price}
                                onChange={(e) => handleUpdateItemPrice(index, parseFloat(e.target.value) || 0)}
                                size="small"
                                sx={{ width: 140 }}
                                inputProps={{ min: 0, step: 0.01 }}
                                InputProps={{
                                  endAdornment: <Typography variant="caption">د.ع</Typography>
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                {(item.quantity * parseFloat(item.price)).toLocaleString()} د.ع
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={item.available ? 'متوفر' : 'غير متوفر'}
                                color={item.available ? 'success' : 'error'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell align="center">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveItem(index)}
                                title="حذف المنتج"
                              >
                                <CancelIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  {/* Mobile Card View */}
                  <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                    {editedOrder.items.map((item, index) => (
                      <Paper 
                        key={index} 
                        sx={{ 
                          p: 2, 
                          mb: 2, 
                          border: '1px solid #e0e0e0',
                          borderRadius: 2,
                          boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          {item.product_image && (
                            <Box
                              component="img"
                              src={item.product_image.startsWith('http') ? 
                                item.product_image : 
                                `${API_BASE_URL}/api/uploads/products/${item.product_image}`
                              }
                              alt={item.product_name}
                              sx={{ 
                                width: 80, 
                                height: 80, 
                                borderRadius: 2, 
                                objectFit: 'cover',
                                flexShrink: 0
                              }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                          )}
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                              {item.product_name}
                            </Typography>
                            {item.product_sku && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                كود: {item.product_sku}
                              </Typography>
                            )}
                            {item.brand_name && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                البراند: {item.brand_name}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1 }}>
                              <Chip
                                label={item.available ? 'متوفر' : 'غير متوفر'}
                                color={item.available ? 'success' : 'error'}
                                size="small"
                              />
                            </Box>
                          </Box>
                        </Box>
                        
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">الكمية</Typography>
                            <TextField
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleUpdateItemQuantity(index, parseInt(e.target.value) || 0)}
                              size="small"
                              fullWidth
                              inputProps={{ min: 0 }}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">السعر (د.ع)</Typography>
                            <TextField
                              type="number"
                              value={item.price}
                              onChange={(e) => handleUpdateItemPrice(index, parseFloat(e.target.value) || 0)}
                              size="small"
                              fullWidth
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Box>
                        
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center',
                          pt: 2,
                          borderTop: '1px solid #e0e0e0'
                        }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#4169E1' }}>
                            الإجمالي: {(item.quantity * parseFloat(item.price)).toLocaleString()} د.ع
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveItem(index)}
                            title="حذف المنتج"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="h6" color="text.secondary">
                      لا توجد عناصر في هذا الطلب
                    </Typography>
                  </Box>
                )}
                
                {/* Items Summary */}
                {editedOrder && editedOrder.items && editedOrder.items.length > 0 && (
                  <Box sx={{ mt: 3, p: { xs: 2, sm: 2 }, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 2, 
                        color: '#4169E1', 
                        fontWeight: 'bold',
                        fontSize: { xs: '1.1rem', sm: '1.25rem' }
                      }}
                    >
                      ملخص الطلب
                    </Typography>
                    <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          <strong>عدد المنتجات:</strong> {editedOrder.items.length}
                        </Typography>
                        <Typography sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          <strong>إجمالي الكمية:</strong> {editedOrder.items.reduce((sum, item) => sum + parseInt(item.quantity), 0)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography sx={{ mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          <strong>إجمالي المنتجات:</strong> {editedOrder.items.reduce((sum, item) => sum + (item.quantity * parseFloat(item.price)), 0).toLocaleString()} د.ع
                        </Typography>
                        <Typography sx={{ mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          <strong>رسوم التوصيل:</strong> {parseFloat(editedOrder.delivery || 0).toLocaleString()} د.ع
                        </Typography>
                        <Typography sx={{ mb: 1, fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                          <strong>الخصم:</strong> {parseFloat(editedOrder.discount_amount || 0).toLocaleString()} د.ع
                        </Typography>
                        <Typography sx={{ 
                          color: '#4169E1', 
                          fontWeight: 'bold', 
                          fontSize: { xs: '1.05rem', sm: '1.1rem' }, 
                          mt: 1, 
                          pt: 1, 
                          borderTop: '2px solid #ddd' 
                        }}>
                          <strong>المبلغ النهائي:</strong> {(
                            editedOrder.items.reduce((sum, item) => sum + (item.quantity * parseFloat(item.price)), 0) + 
                            parseFloat(editedOrder.delivery || 0) - 
                            parseFloat(editedOrder.discount_amount || 0)
                          ).toLocaleString()} د.ع
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions sx={{ 
          p: { xs: 2, sm: 3 }, 
          pt: { xs: 1, sm: 1 },
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 }
        }}>
          <Button 
            onClick={() => setDetailsDialogOpen(false)} 
            variant="outlined"
            fullWidth={isMobile}
            sx={{ order: { xs: 2, sm: 1 } }}
          >
            إغلاق
          </Button>
          <Button 
            onClick={handleSaveOrderChanges} 
            variant="contained" 
            sx={{ 
              mr: { xs: 0, sm: 1 },
              order: { xs: 1, sm: 2 }
            }}
            disabled={!hasOrderChanges() || loading}
            fullWidth={isMobile}
          >
            {loading ? 'جاري الحفظ...' : 'حفظ التغييرات'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Update Status Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          تحديث حالة الطلب #{selectedOrder?.id}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>الحالة الجديدة</InputLabel>
            <Select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              label="الحالة الجديدة"
            >
              {statusOptions.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {status.icon}
                    <Typography sx={{ ml: 1 }}>{status.label}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>إلغاء</Button>
          <Button onClick={handleUpdateOrderStatus} variant="contained">
            تحديث
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog 
        open={addProductDialogOpen} 
        onClose={() => setAddProductDialogOpen(false)} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            إضافة منتج إلى الطلب #{selectedOrder?.id}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          {/* Search and Filters Row */}
          <Box sx={{ mb: 3 }}>
            {/* Product Search */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="البحث عن منتج (الاسم، الكود، البراند)..."
              value={productSearchQuery}
              onChange={(e) => setProductSearchQuery(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Filters Row */}
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>الفئة</InputLabel>
                  <Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    label="الفئة"
                  >
                    <MenuItem value="">
                      <em>جميع الفئات</em>
                    </MenuItem>
                    {categories.map((category) => (
                      <MenuItem key={category.id} value={category.id}>
                        {category.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>البراند</InputLabel>
                  <Select
                    value={brandFilter}
                    onChange={(e) => setBrandFilter(e.target.value)}
                    label="البراند"
                  >
                    <MenuItem value="">
                      <em>جميع البراندات</em>
                    </MenuItem>
                    {brands.map((brand) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            {/* Clear Filters Button */}
            {(categoryFilter || brandFilter || productSearchQuery) && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    setCategoryFilter('');
                    setBrandFilter('');
                    setProductSearchQuery('');
                  }}
                  startIcon={<CancelIcon />}
                >
                  مسح الفلاتر
                </Button>
              </Box>
            )}
          </Box>

          {/* Products List */}
          {loadingProducts ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>جاري تحميل المنتجات...</Typography>
            </Box>
          ) : availableProducts.length === 0 && (productSearchQuery.trim().length < 2 && !categoryFilter && !brandFilter) ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <SearchIcon sx={{ fontSize: 60, color: '#9e9e9e', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                ابدأ بالبحث عن المنتج أو اختر فلتر
              </Typography>
              <Typography variant="body2" color="text.secondary">
                اكتب اسم المنتج (حرفين على الأقل)، الكود، أو اختر فئة/براند للبحث
              </Typography>
            </Box>
          ) : availableProducts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                لا توجد نتائج للبحث
              </Typography>
              <Typography variant="body2" color="text.secondary">
                جرب البحث بكلمات أخرى
              </Typography>
            </Box>
          ) : (
            <>
            <Box sx={{ maxHeight: 400, overflowY: 'auto', mb: 3 }}>
              {getFilteredProducts().map((product) => {
                const isSelected = selectedProducts.some(sp => sp.product.id === product.id);
                return (
                  <Paper
                    key={product.id}
                    sx={{
                      p: 2,
                      mb: 2,
                      border: isSelected ? '2px solid #4169E1' : '1px solid #e0e0e0',
                      backgroundColor: isSelected ? '#f0f4ff' : 'white',
                      '&:hover': { backgroundColor: '#f8f9fa' }
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={1}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProducts([...selectedProducts, { product, quantity: 1 }]);
                            } else {
                              setSelectedProducts(selectedProducts.filter(sp => sp.product.id !== product.id));
                            }
                          }}
                          sx={{ color: '#4169E1' }}
                        />
                      </Grid>
                      <Grid item xs={2}>
                        {(product.image_url || product.image) && (
                          <Box
                            component="img"
                            src={(product.image_url || product.image).startsWith('http') ? 
                              (product.image_url || product.image) : 
                              `${API_BASE_URL}/api/uploads/products/${product.image_url || product.image}`
                            }
                            alt={product.name}
                            sx={{ 
                              width: 60, 
                              height: 60, 
                              borderRadius: 1,
                              objectFit: 'cover'
                            }}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        )}
                      </Grid>
                      <Grid item xs={5}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          {product.name}
                        </Typography>
                        {product.sku && (
                          <Typography variant="caption" color="text.secondary">
                            كود المنتج: {product.sku}
                          </Typography>
                        )}
                        {product.barcode && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            الباركود: {product.barcode}
                          </Typography>
                        )}
                        {product.brand_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            البراند: {product.brand_name}
                          </Typography>
                        )}
                        {product.category_name && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            الفئة: {product.category_name}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={2}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4169E1' }}>
                          {parseFloat(product.after_discount || product.sell_price || product.price || 0).toLocaleString()} د.ع
                        </Typography>
                        {product.after_discount && (product.sell_price || product.price) && parseFloat(product.after_discount) < parseFloat(product.sell_price || product.price) && (
                          <Typography variant="caption" sx={{ display: 'block', textDecoration: 'line-through', color: 'text.secondary' }}>
                            {parseFloat(product.sell_price || product.price).toLocaleString()} د.ع
                          </Typography>
                        )}
                        {product.purchase_price && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            سعر الشراء: {parseFloat(product.purchase_price).toLocaleString()} د.ع
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={2}>
                        <Chip
                          label={(product.active === 1 || product.active === '1') ? 'متوفر' : 'غير متوفر'}
                          color={(product.active === 1 || product.active === '1') ? 'success' : 'error'}
                          size="small"
                        />
                        {product.stock !== undefined && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                            الكمية: {product.stock || 'غير محدد'}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                );
              })}
            </Box>

            {/* Selected Products Section */}
            {selectedProducts.length > 0 && (
              <Box sx={{ mt: 3, p: 3, backgroundColor: '#f0f7ff', borderRadius: 2, border: '2px solid #4169E1' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#4169E1' }}>
                    المنتجات المحددة ({selectedProducts.length})
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => setSelectedProducts([])}
                  >
                    مسح الكل
                  </Button>
                </Box>
                
                <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                  {selectedProducts.map((selectedItem, index) => {
                    const product = selectedItem.product;
                    const finalPrice = parseFloat(product.after_discount || product.sell_price || product.price || 0);
                    const totalPrice = selectedItem.quantity * finalPrice;
                    
                    return (
                      <Paper key={product.id} sx={{ p: 2, mb: 2, backgroundColor: 'white' }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={1}>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setSelectedProducts(selectedProducts.filter(sp => sp.product.id !== product.id));
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Grid>
                          <Grid item xs={1}>
                            {(product.image_url || product.image) && (
                              <Box
                                component="img"
                                src={(product.image_url || product.image).startsWith('http') ? 
                                  (product.image_url || product.image) : 
                                  `${API_BASE_URL}/api/uploads/products/${product.image_url || product.image}`
                                }
                                alt={product.name}
                                sx={{ 
                                  width: 50, 
                                  height: 50, 
                                  borderRadius: 1,
                                  objectFit: 'cover'
                                }}
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                }}
                              />
                            )}
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                              {product.name}
                            </Typography>
                            {product.sku && (
                              <Typography variant="caption" color="text.secondary">
                                كود: {product.sku}
                              </Typography>
                            )}
                          </Grid>
                          <Grid item xs={2}>
                            <TextField
                              type="number"
                              label="الكمية"
                              size="small"
                              value={selectedItem.quantity}
                              onChange={(e) => {
                                const newQty = parseInt(e.target.value) || 1;
                                const updatedProducts = [...selectedProducts];
                                updatedProducts[index].quantity = newQty;
                                setSelectedProducts(updatedProducts);
                              }}
                              inputProps={{ min: 1, max: product.stock }}
                              sx={{ width: '100%' }}
                            />
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="body2" color="text.secondary">
                              السعر: {finalPrice.toLocaleString()} د.ع
                            </Typography>
                          </Grid>
                          <Grid item xs={2}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#4169E1' }}>
                              {totalPrice.toLocaleString()} د.ع
                            </Typography>
                          </Grid>
                        </Grid>
                      </Paper>
                    );
                  })}
                </Box>
                
                <Box sx={{ mt: 2, pt: 2, borderTop: '2px solid #e0e0e0' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center', color: '#4169E1' }}>
                    المجموع الكلي: {selectedProducts.reduce((total, item) => {
                      const price = parseFloat(item.product.after_discount || item.product.sell_price || item.product.price || 0);
                      return total + (item.quantity * price);
                    }, 0).toLocaleString()} د.ع
                  </Typography>
                </Box>
              </Box>
            )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setAddProductDialogOpen(false)} variant="outlined">
            إلغاء
          </Button>
          <Button 
            onClick={handleAddProductToOrder} 
            variant="contained"
            disabled={selectedProducts.length === 0}
            sx={{ mr: 1 }}
            startIcon={<AddIcon />}
          >
            إضافة {selectedProducts.length > 0 ? `(${selectedProducts.length})` : ''} إلى الطلب
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* PDF Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ExportIcon sx={{ mr: 2, color: '#4169E1' }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              تصدير الطلبات
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              عدد الطلبات المحددة: <strong>{selectedOrdersForExport.length}</strong>
            </Typography>
            
            {selectedOrdersForExport.length > 0 ? (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  سيتم تصدير الطلبات التالية:
                </Typography>
                <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
                  {selectedOrdersForExport.map(orderId => {
                    const order = filteredOrders.find(o => o.id === orderId);
                    return order ? (
                      <Box key={orderId} sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid #f0f0f0' }}>
                        <Typography variant="body2">
                          طلب #{order.id} - {order.customer_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {parseFloat(order.total_amount).toLocaleString()} د.ع
                        </Typography>
                      </Box>
                    ) : null;
                  })}
                </Box>
                
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                    خيارات التصدير:
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    • <strong>تحديث المخزون (Excel):</strong> ملف Excel يحتوي على المنتجات لتحديث النظام المحاسبي
                  </Typography>
                  <Typography variant="body2">
                    • <strong>تصدير PDF:</strong> ملف PDF يحتوي على تفاصيل الطلبات الكاملة
                  </Typography>
                </Alert>
              </Box>
            ) : (
              <Alert severity="warning">
                <Typography variant="body2">
                  يرجى اختيار طلبات من الجدول أولاً للتصدير
                </Typography>
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setExportDialogOpen(false)} variant="outlined">
            إلغاء
          </Button>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button 
              onClick={generateStockExport} 
              variant="contained"
              disabled={selectedOrdersForExport.length === 0 || exportingPdf}
              startIcon={exportingPdf ? null : <ExportIcon />}
              sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}
            >
              {exportingPdf ? 'جاري التصدير...' : 'تحديث المخزون'}
            </Button>
            <Button 
              onClick={generateOrdersPDF} 
              variant="contained"
              disabled={selectedOrdersForExport.length === 0 || exportingPdf}
              startIcon={exportingPdf ? null : <PdfIcon />}
              sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
            >
              {exportingPdf ? 'جاري التصدير...' : 'تصدير PDF'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Create Order Dialog */}
      <Dialog 
        open={createOrderDialogOpen} 
        onClose={() => setCreateOrderDialogOpen(false)}
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          إنشاء طلب جديد
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="مصدر الطلب *"
                  value={newOrder.customer_name}
                  onChange={(e) => setNewOrder({ ...newOrder, customer_name: e.target.value })}
                  variant="outlined"
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="">اختر المصدر</option>
                  <option value="facebook">Facebook</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="instagram">Instagram</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="رقم الهاتف *"
                  value={newOrder.customer_phone}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                    if (value.length <= 11) {
                      setNewOrder({ ...newOrder, customer_phone: value });
                      if (value.length === 11 && validatePhoneNumber(value)) {
                        setPhoneError('');
                      } else if (value.length > 0) {
                        setPhoneError('رقم الهاتف يجب أن يبدأ بـ 07 ويتكون من 11 رقم');
                      } else {
                        setPhoneError('');
                      }
                    }
                  }}
                  variant="outlined"
                  placeholder="07XXXXXXXXX"
                  error={!!phoneError}
                  helperText={phoneError || 'مثال: 07712345678'}
                  inputProps={{
                    maxLength: 11,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="المدينة *"
                  value={newOrder.city}
                  onChange={(e) => {
                    setNewOrder({ ...newOrder, city: e.target.value });
                    setShowCustomCity(e.target.value === 'أخرى');
                    if (e.target.value !== 'أخرى') {
                      setCustomCity('');
                    }
                  }}
                  variant="outlined"
                  SelectProps={{
                    native: true
                  }}
                >
                  <option value="">اختر المدينة</option>
                  {cities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                  <option value="أخرى">أخرى</option>
                </TextField>
              </Grid>
              {showCustomCity && (
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="اسم المدينة *"
                    value={customCity}
                    onChange={(e) => setCustomCity(e.target.value)}
                    variant="outlined"
                    placeholder="أدخل اسم المدينة"
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="العنوان الكامل *"
                  value={newOrder.shipping_address}
                  onChange={(e) => setNewOrder({ ...newOrder, shipping_address: e.target.value })}
                  variant="outlined"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="حالة الطلب"
                  value={newOrder.status}
                  onChange={(e) => setNewOrder({ ...newOrder, status: e.target.value })}
                  variant="outlined"
                  SelectProps={{
                    native: true
                  }}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="ملاحظات العميل"
                  value={newOrder.notes}
                  onChange={(e) => setNewOrder({ ...newOrder, notes: e.target.value })}
                  variant="outlined"
                  multiline
                  rows={3}
                  placeholder="أي ملاحظات من العميل..."
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              بعد إنشاء الطلب، يمكنك إضافة المنتجات من صفحة تفاصيل الطلب
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={() => setCreateOrderDialogOpen(false)} 
            variant="outlined"
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleCreateOrder} 
            variant="contained"
            disabled={creatingOrder}
            sx={{ backgroundColor: '#4169E1' }}
          >
            {creatingOrder ? 'جاري الإنشاء...' : 'إنشاء الطلب'}
          </Button>
        </DialogActions>
      </Dialog>
        </Box>
      </div>
    </ThemeProvider>
  );
};

export default OrderManagement;
